/**
 * Order Detail Tab Components
 * Contains the main tab views for the order details page:
 * - OverviewTab: Order overview with customer and property info
 * - SubmissionsTab: Submission information
 * - FulfillmentTab: Tax certificate fulfillment interface
 * - ActivityTab: Order activity history with pagination
 * 
 * Extracted from App.js to improve maintainability.
 */

import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import { dedupedGet } from '../../../lib/requestDedupe';
import { formatInTimeZone } from 'date-fns-tz';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../ui/button';
import ScrollToTopButton from '../../ui/ScrollToTopButton';
import { useAuth } from '../../../App';
import { TaxCertificatePanel } from '../../tax';
import { normalizeBorrowerName } from '../../../utils/nameNormalizer';
import SearchFulfillmentPanel from '../../search/SearchFulfillmentPanel';
import { holdsRole } from '../../../utils/heldRoles';
import {
  formatStreetAddress,
  formatCity,
  formatState,
  formatCounty,
  EMPTY_PLACEHOLDER,
} from '../../../utils/textFormat';
import OrderBillingPanel from '../OrderBillingPanel';
import OperationalFeesPanel from '../OperationalFeesPanel';
import { SectionHeader, EnhancedCard } from '../../shared';
import { isTaxProduct, isSearchProduct } from '../../../utils/orderHelpers';
import { getRoleDashboardPath } from '../../../utils/roleDashboard';
import { FEATURE_FLAGS } from '../../../utils/featureFlags';
import MailAwayModal from '../../MailAwayModal';
import { ReturnToMailAwayDialog, activeMailAwayRequests, PendingOverrideDialog } from '../ReturnToMailAwayDialog';
import { CycleAckModal } from '../../MultiJurisdictionMailAway';
import { computeDelinquentGroups } from '../../../lib/paDelinquentGrouping';
import { toast } from 'sonner';
import ReviewTaxCertModal from './ReviewTaxCertModal';
import CallNotesScratchpad from './CallNotesScratchpad';
import ProcessorFeedbackBanner from '../../qc/ProcessorFeedbackBanner';
import QcOverrideCompleteButton from '../../qc/QcOverrideCompleteButton';
import TypingReturnBanner from '../../typing/TypingReturnBanner';
import QCReviewBar from '../../qc/QCReviewBar';
import {
  ChevronLeft,
  ChevronRight,
  Clock,
  User,
  FileText,
  AlertCircle,
  CheckCircle,
  MapPin,
  ClipboardList,
  Mail,
  Send,
  Upload,
  Link,
  DollarSign,
  Loader2,
  RotateCcw,
  Users,
  Tag,
  Briefcase,
  Calendar,
  ChevronUp,
  CheckCircle2,
} from 'lucide-react';
import '../order-overview-premium.css';
import { formatDateOnly } from '../../../utils/formatters';
import { ContractCodeChip } from '../ProductWithCode';
import { showTaxEmailAutomationToasts } from '../../../utils/taxEmailToasts';
import { qcComponentOf } from '../../../lib/qcComponent';

const API = process.env.REACT_APP_BACKEND_URL 
  ? `${process.env.REACT_APP_BACKEND_URL}/api` 
  : '/api';

const ACCOUNT_TZ = 'America/New_York';

// Helper to format date
const formatDate = (dateString) => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return formatDateOnly(date);
  } catch {
    return dateString;
  }
};

// Helper to ensure timestamp is treated as UTC (backend returns timestamps without 'Z')
const ensureUtcTimestamp = (timestamp) => {
  if (!timestamp) return null;
  if (timestamp instanceof Date) return timestamp.toISOString();
  const str = String(timestamp);
  if (!str.endsWith('Z') && !str.match(/[+-]\d{2}:\d{2}$/)) {
    return str + 'Z';
  }
  return str;
};

// Helper to format date with time for activity events (with timezone)
// V1 REQUIREMENT: All timestamps displayed in America/New_York (ET)
const formatDateTime = (dateString) => {
  if (!dateString) return '';
  try {
    const utcTimestamp = ensureUtcTimestamp(dateString);
    return formatInTimeZone(utcTimestamp, ACCOUNT_TZ, 'MM/dd/yyyy h:mm a') + ' ET';
  } catch {
    return dateString;
  }
};

// Helper: relative "Xm ago / Xh ago / Xd ago" used by the QC Review header strip.
const formatRelativeAgo = (dateString) => {
  if (!dateString) return null;
  try {
    const d = new Date(ensureUtcTimestamp(dateString));
    if (isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    const diffSec = Math.max(0, Math.floor(diffMs / 1000));
    if (diffSec < 60) return `${diffSec}s ago`;
    const diffMin = Math.floor(diffSec / 60);
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    return `${diffDay}d ago`;
  } catch {
    return null;
  }
};

const OverviewTab = ({ orderData, onOrderUpdate, onNavigateToTab, effectiveRole }) => {
  const { user } = useAuth();
  // DUAL-ROLE-OVERVIEW-001: Operational Fees follows the active workspace role; Billing stays on the raw role.
  const opsRole = effectiveRole || user?.role;
  
  // TITLEFLOW-ORDER-OVERVIEW-UI-001 — presentation-only premium layout (values unchanged)
  const [billingOpen, setBillingOpen] = React.useState(true);
  const statusKey = (orderData.status || 'open').toLowerCase();
  const statusTone = ['complete', 'completed'].includes(statusKey) ? 'done' : ['pending', 'clarification', 'on_hold'].includes(statusKey) ? 'warn' : statusKey === 'cancelled' ? 'danger' : 'active';
  const apnRaw = orderData.property?.parcel_id || orderData.parcel_apn;
  const apnStr = apnRaw ? String(apnRaw) : '';
  const apnInline = apnStr && apnStr.length <= 22 && !apnStr.includes(',') && !apnStr.includes(';');
  const metaItems = [
    { key: 'customer', label: 'Customer', Icon: User, bg: '#eff6ff', c: '#2563eb', testId: 'operational-header-customer',
      value: normalizeBorrowerName(orderData.customer?.name || orderData.customer?.customer_name || orderData.borrower_name || orderData.customer_name) || 'N/A' },
    ...(orderData.seller_name ? [{ key: 'seller', label: 'Seller', Icon: Users, bg: '#fff7ed', c: '#ea580c', testId: 'operational-header-seller', value: normalizeBorrowerName(orderData.seller_name) || orderData.seller_name }] : []),
    { key: 'product', label: 'Product', Icon: FileText, bg: '#f5f3ff', c: '#7c3aed', testId: 'operational-header-product', value: orderData.client_product,
      extra: (<>
        <ContractCodeChip code={orderData.contract_code} testId="overview-contract-code" size="xs" />
        {(orderData.product_id || orderData.external_task_id || orderData.task_id || orderData.external_loan_id) && (
          <span className="font-mono text-slate-500 text-[12px] ml-1">· {orderData.product_id || orderData.external_task_id || orderData.task_id || orderData.external_loan_id}</span>
        )}
      </>) },
    { key: 'order', label: 'Order #', Icon: Tag, bg: '#eef2ff', c: '#4f46e5', testId: 'operational-header-order-number-meta', value: orderData.order_number, mono: true },
    { key: 'client', label: 'Client', Icon: Briefcase, bg: '#ecfdf5', c: '#059669', testId: 'operational-header-client', value: orderData.client_code },
  ];

  return (
    <div className="od-wide py-3 ov-premium">
      {/* ── Order Information header ── */}
      <div className="ov-head ov-enter mb-3" data-testid="overview-order-header">
        <div className="ov-band">
          <div className="relative flex items-center gap-3 min-w-0 flex-wrap">
            <span className="ov-band-label">Order</span>
            <span className="ov-band-number font-mono" data-testid="operational-header-order-number">{orderData.order_number}</span>
            <span className="ov-status" data-tone={statusTone} data-testid="operational-header-status">
              {statusTone === 'done' ? <CheckCircle2 className="w-3.5 h-3.5" /> : <span className="w-1.5 h-1.5 rounded-full bg-current opacity-80" />}
              {orderData.status?.replace(/_/g, ' ') || 'open'}
            </span>
          </div>
          <div className="relative flex items-center gap-4 flex-shrink-0">
            <span className="ov-created" data-testid="operational-header-created"><Calendar className="w-3.5 h-3.5 opacity-90" /> Created {formatDate(orderData.created_at)}</span>
            <span className="ov-art hidden md:inline-flex" aria-hidden="true">
              <FileText className="text-white/95" />
              {statusTone === 'done' && <span className="ov-art-check"><CheckCircle className="w-3 h-3" /></span>}
            </span>
          </div>
        </div>
        <div className="ov-meta">
          {metaItems.map(m => (
            <div key={m.key} className="ov-meta-item">
              <span className="ov-tile-ic" style={{ '--ic-bg': m.bg, '--ic-c': m.c }}><m.Icon /></span>
              <div className="min-w-0">
                <p className="ov-meta-label">{m.label}</p>
                <p className={`ov-meta-value truncate ${m.mono ? 'font-mono text-[13px]' : ''}`} data-testid={m.testId}>{m.value}{m.extra}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="ov-prop">
          <span className="ov-tile-ic" style={{ '--ic-bg': '#fff1f2', '--ic-c': '#e11d48' }}><MapPin /></span>
          <div className="min-w-0">
            <p className="ov-meta-label">Property</p>
            <p className="ov-prop-value">
              <span data-testid="operational-header-address">{formatStreetAddress(orderData.property_address_full || orderData.property_address || orderData.property?.address || orderData.property?.property_address) || 'N/A'}</span>
              <span className="ov-sep">·</span><span className="text-slate-700 font-medium">{formatCity(orderData.property_city || orderData.property?.city || orderData.property?.property_city)}</span>
              <span className="ov-sep">·</span><span className="text-slate-700 font-medium">{formatCounty(orderData.property_county || orderData.property?.county || orderData.property?.property_county || orderData.county)}</span>
              <span className="ov-sep">·</span><span className="text-slate-700 font-medium">{formatState(orderData.property_state || orderData.property?.state || orderData.property?.property_state)}</span>
              <span className="ov-sep">·</span><span className="text-slate-700 font-medium">{orderData.property_zip_code || orderData.property?.zip_code || orderData.property?.property_zip_code || orderData.property_zip || EMPTY_PLACEHOLDER}</span>
              {apnStr && (apnInline
                ? <><span className="ov-sep">·</span><span className="text-slate-500 font-mono text-[12px]" data-testid="operational-header-apn">APN {apnStr}</span></>
                : <><span className="ov-sep">·</span><button type="button" className="text-[12px] text-blue-600 hover:text-blue-700 hover:underline" onClick={() => { if (typeof window !== 'undefined') window.location.hash = '#parcels'; }} data-testid="operational-header-view-parcels">View Parcels →</button></>)}
            </p>
          </div>
        </div>
      </div>

      {/* ── Billing Information (Admin / Accounting only — unchanged gate) ── */}
      {(user?.role === 'admin' || user?.role === 'accounting') && (
        <div className="ov-card ov-enter-2 mb-3" data-accent="blue" data-testid="overview-billing-card">
          <div className="ov-card-head">
            <div className="flex items-center gap-3 min-w-0">
              <span className="ov-card-ic"><DollarSign /></span>
              <div className="min-w-0">
                <h3 className="ov-card-title">Billing Information</h3>
                <p className="ov-card-sub">Client pricing and billing summary for this order</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="ov-chip" data-tone={(orderData.billing_status || 'unbilled').toLowerCase()} data-testid="overview-billing-status">
                <Clock className="w-3 h-3" /> {orderData.billing_status || 'Unbilled'}
              </span>
              <button type="button" className="ov-collapse" aria-expanded={billingOpen} onClick={() => setBillingOpen(o => !o)} aria-label={billingOpen ? 'Collapse billing' : 'Expand billing'} data-testid="overview-billing-toggle"><ChevronUp className="w-3.5 h-3.5" /></button>
            </div>
          </div>
          <div className="ov-body-wrap" data-open={billingOpen}>
            <div>
              <div className="ov-body">
          <OrderBillingPanel 
            order={orderData} 
            userRole={user?.role}
            compact={true}
            onUpdate={() => {
              if (onOrderUpdate) {
                axios.get(`${API}/orders/${orderData.id}/details`)
                  .then(response => {
                    const combinedData = {
                      ...response.data.order,
                      property: response.data.property,
                      customer: response.data.customer
                    };
                    onOrderUpdate(combinedData);
                  })
                  .catch(console.error);
              }
            }}
          />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════════════════════════════════════════════════════════
          SECTION 4b: Operational Fees Panel — Tax Search Team Leader only
          (May 17, 2026 RBAC). Mounted as a STRUCTURALLY DIFFERENT
          component (not a flag on OrderBillingPanel) so Base Price,
          Margin, Internal Cost, Override, and Recalculate cannot leak
          into the Tax Team Leader DOM.
          ═══════════════════════════════════════════════════════════════ */}
      {/* OPS-FEE-TREATMENT-001: Admin/Accounting also mount the panel — they are the
          only roles allowed to change a saved fee's Billing Treatment. */}
      {/* TITLE-QC-DOC-EXPENSE-001: QC Specialist (title scope) mounts the panel on Title Search orders to record
          document-purchase expenses — backend verify_paid_site_fee_access enforces scope/workflow/own-entry rules. */}
      {['tax_search_team_leader', 'title_search_team_leader', 'title_search_processor', 'search_processor', 'typing_processor', 'admin', 'accounting', 'qc_specialist'].includes((opsRole || '').toLowerCase()) && (
        <div className="mb-3 ov-enter-3">
          <OperationalFeesPanel
            order={orderData}
            userRole={opsRole}
            currentUserId={user?.id}
            onUpdate={() => {
              if (onOrderUpdate) {
                axios.get(`${API}/orders/${orderData.id}/details`)
                  .then(response => {
                    const combinedData = {
                      ...response.data.order,
                      property: response.data.property,
                      customer: response.data.customer
                    };
                    onOrderUpdate(combinedData);
                  })
                  .catch(console.error);
              }
            }}
          />
        </div>
      )}
    </div>
  );
};

const SubmissionsTab = ({ orderData }) => {
  return (
    <div className="max-w-7xl mx-auto px-6 py-8">
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">Submission Information</h2>
        </div>
        <div className="p-6">
          {/* Acceptance Banner */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
            <div className="flex items-center gap-2">
              <div className="w-4 h-4 bg-green-500 rounded-full flex items-center justify-center">
                <span className="text-white text-xs">✓</span>
              </div>
              <p className="text-green-800 font-medium">
                This order was accepted on {formatDate(orderData.created_at)}
              </p>
            </div>
          </div>

          <div className="space-y-6">
            {/* Submission Fields */}
            <div>
              <h3 className="text-md font-medium text-gray-900 mb-4">Submission Details</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="text-sm font-medium text-gray-600">Client Product</label>
                  <p className="text-gray-900 flex items-center gap-1.5">{orderData.client_product} <ContractCodeChip code={orderData.contract_code} testId="submissions-contract-code" /></p>
                </div>
                <div>
                  <label className="text-sm font-medium text-gray-600">Product ID</label>
                  <p className="text-gray-900">{orderData.product_id || orderData.external_task_id || orderData.task_id || orderData.external_loan_id || 'N/A'}</p>
                </div>
              </div>
            </div>

            {/* Parcel IDs */}
            {orderData.property?.parcel_id && (
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">Parcel Information</h3>
                <div className="bg-gray-50 rounded-lg p-4">
                  <label className="text-sm font-medium text-gray-600">Parcel ID / APN</label>
                  <p className="text-gray-900 font-mono">{orderData.property.parcel_id}</p>
                </div>
              </div>
            )}

            {/* Notes */}
            {orderData.notes && (
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">Submission Notes</h3>
                <div className="bg-gray-50 rounded-lg p-4 max-h-32 overflow-y-auto">
                  <p className="text-gray-900 whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{orderData.notes}</p>
                </div>
              </div>
            )}

            {orderData.priority_notes && (
              <div>
                <h3 className="text-md font-medium text-gray-900 mb-4">Priority Instructions</h3>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 max-h-32 overflow-y-auto">
                  <p className="text-gray-900 whitespace-pre-wrap" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>{orderData.priority_notes}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const FulfillmentTab = ({ orderData, onOrderUpdate, onRefreshOrder, onNavigateToTab }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  
  // Sticky summary card detection
  const stickySentinelRef = useRef(null);
  const [isSummaryStuck, setIsSummaryStuck] = useState(false);

  useEffect(() => {
    const sentinel = stickySentinelRef.current;
    if (!sentinel) return;
    const observer = new IntersectionObserver(
      ([entry]) => setIsSummaryStuck(!entry.isIntersecting),
      { threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, []);
  
  // Shared certificate data state - accessible by both TaxCertificatePanel and FinalizeOrderCard
  const [sharedCertificateData, setSharedCertificateData] = useState(null);
  
  // Workflow Actions State - lifted to FulfillmentTab for shared access
  // Initialize from orderData: if order is already Pending/Clarification, pre-select status button
  // Textarea always starts EMPTY for new append-mode entry
  const [workflowSelectedStatus, setWorkflowSelectedStatus] = useState(() => {
    const status = orderData?.status;
    return (status === 'pending' || status === 'clarification') ? status : null;
  });
  const [workflowReason, setWorkflowReason] = useState('');
  const [workflowIsSubmitting, setWorkflowIsSubmitting] = useState(false);
  // Jun 2026 — completion success popup. The old flow fired a 2s toast and
  // navigated away 1.5s later: the notification was effectively invisible
  // (user-reported "no completion popup appears"). A blocking success
  // dialog now confirms completion, then redirects.
  const [completionSuccess, setCompletionSuccess] = useState(null);
  // QC-VAL-POPUP-001 — missing-required-fields modal for QC Review submission
  const [qcValidationModal, setQcValidationModal] = useState(null); // { message, missingFields }
  const completionRedirectRef = useRef(false);
  // COMPLETE-BTN-FEEDBACK-001: persistent inline reason under the Complete button + in-flight guard
  const [completeError, setCompleteError] = useState(null);
  const completeInFlightRef = useRef(false);
  const clearCompleteError = useCallback(() => setCompleteError(null), []);
  // Correcting the order (status change / refetch after upload etc.) clears the stale reason
  useEffect(() => { setCompleteError(null); }, [orderData?.status, orderData?.updated_at]);
  const [workflowEditMode, setWorkflowEditMode] = useState(false);
  const prevWorkflowStatusRef = useRef(
    (orderData?.status === 'pending' || orderData?.status === 'clarification') ? orderData.status : null
  );
  
  // Workflow Integrity: Clear notes when switching statuses OR deselecting
  useEffect(() => {
    const prevStatus = prevWorkflowStatusRef.current;
    if (prevStatus && prevStatus !== workflowSelectedStatus) {
      // Status changed (switched to different status) or deselected (went to null)
      setWorkflowReason('');
    }
    prevWorkflowStatusRef.current = workflowSelectedStatus;
  }, [workflowSelectedStatus]);
  
  // Smart Auto-Redirect state after Pending/Clarification submission
  const [pendingRedirect, setPendingRedirect] = useState(null); // { status, countdown }
  const redirectTimerRef = useRef(null);
  const redirectCountdownRef = useRef(null);
  
  // Cancel the pending redirect
  const cancelRedirect = React.useCallback(() => {
    if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
    if (redirectCountdownRef.current) clearInterval(redirectCountdownRef.current);
    redirectTimerRef.current = null;
    redirectCountdownRef.current = null;
    setPendingRedirect(null);
  }, []);
  
  // Start the smart redirect countdown
  const startSmartRedirect = React.useCallback((statusLabel) => {
    const REDIRECT_DELAY = 2; // seconds
    setPendingRedirect({ status: statusLabel, countdown: REDIRECT_DELAY });
    
    // Countdown interval
    let remaining = REDIRECT_DELAY;
    redirectCountdownRef.current = setInterval(() => {
      remaining -= 1;
      if (remaining <= 0) {
        clearInterval(redirectCountdownRef.current);
        redirectCountdownRef.current = null;
      } else {
        setPendingRedirect(prev => prev ? { ...prev, countdown: remaining } : null);
      }
    }, 1000);
    
    // Actual redirect after delay
    redirectTimerRef.current = setTimeout(() => {
      redirectTimerRef.current = null;
      setPendingRedirect(null);
      // Feb 27, 2026 — Role-aware landing. Processors go to
      // /dashboard/processor-orders, QC to /qc-dashboard, etc.
      // Previously hard-coded to /dashboard which sent processors to
      // the admin-ish overview instead of their own queue.
      navigate(getRoleDashboardPath(user), { replace: true });
    }, REDIRECT_DELAY * 1000);
  }, [navigate, user]);
  
  // Auto-cancel redirect on ANY user interaction (typing, clicking, editing)
  useEffect(() => {
    if (!pendingRedirect) return;
    
    const handleInteraction = () => {
      cancelRedirect();
      toast.info('Redirect cancelled — staying on page', { duration: 1500 });
    };
    
    // Listen for meaningful interactions only (not the success banner itself)
    document.addEventListener('keydown', handleInteraction, { once: true });
    // Use capture phase + small delay to avoid the click that triggered the submit
    const clickTimer = setTimeout(() => {
      document.addEventListener('mousedown', handleInteraction, { once: true });
    }, 300);
    
    return () => {
      document.removeEventListener('keydown', handleInteraction);
      document.removeEventListener('mousedown', handleInteraction);
      clearTimeout(clickTimer);
    };
  }, [pendingRedirect, cancelRedirect]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) clearTimeout(redirectTimerRef.current);
      if (redirectCountdownRef.current) clearInterval(redirectCountdownRef.current);
    };
  }, []);
  
  // Tax Sheet Review state — sourced from orderData, updated on review action
  const [taxSheetReview, setTaxSheetReview] = useState(
    orderData?.tax_sheet_review || { status: 'pending' }
  );
  
  // Keep review state in sync when orderData refreshes (e.g. after auto-save resets it)
  useEffect(() => {
    if (orderData?.tax_sheet_review) {
      setTaxSheetReview(orderData.tax_sheet_review);
    }
  }, [orderData?.tax_sheet_review]);
  
  // Listen for review status updates from auto-save (dispatched by TaxCertificatePanel)
  useEffect(() => {
    const handleReviewUpdate = (event) => {
      setTaxSheetReview(event.detail);
    };
    window.addEventListener('taxSheetReviewUpdate', handleReviewUpdate);
    return () => window.removeEventListener('taxSheetReviewUpdate', handleReviewUpdate);
  }, []);
  
  // Tax Sheet Review Modal state — only the open flag lives here;
  // all other state (checkbox, PDF, submitting) is inside ReviewTaxCertModal
  const [reviewModalOpen, setReviewModalOpen] = useState(false);

  // MLS-UI-CLEANUP (Aug 2026): mirror of the dual-workspace tab living inside
  // TaxCertificatePanel (dispatched via 'mls-ws-tab-change'). Used ONLY to
  // hide the Tax "Mail-Away (All Jurisdictions)" banner while the MLS
  // workspace is active. Default mirrors TaxCertificatePanel's default.
  const isMlsDualOrder = !!orderData?.components?.mls;
  const [mlsWsTab, setMlsWsTab] = useState(null);
  const effectiveMlsWsTab = mlsWsTab
    || (orderData?.components?.mls?.status === 'complete' ? 'tax' : 'mls');
  useEffect(() => {
    const handleMlsTab = (event) => setMlsWsTab(event.detail?.tab || null);
    window.addEventListener('mls-ws-tab-change', handleMlsTab);
    return () => window.removeEventListener('mls-ws-tab-change', handleMlsTab);
  }, []);

  // Determine if order is from external portal — uses the hierarchical
  // resolver (Order > Product > Contract Mapping > Client > Legacy) so
  // products like TAX LTGDT / Tax-MCTDT (whose external_portal signal
  // comes from the product/mapping/client levels, not the order itself)
  // also get the simplified UI. The local `delivery_method/created_via`
  // check is the initial optimistic guess until the resolver responds.
  const [isExternalPortalOrder, setIsExternalPortalOrder] = useState(
    orderData?.delivery_method === 'external_portal' || orderData?.created_via === 'external_portal'
  );
  useEffect(() => {
    let cancelled = false;
    const resolve = async () => {
      const id = orderData?.id;
      if (!id) return;
      try {
        const token = localStorage.getItem('token');
        const resp = await fetch(`${API}/orders/${id}/external-portal-status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!resp.ok) return;
        const data = await resp.json();
        if (!cancelled) setIsExternalPortalOrder(!!data?.is_external_portal);
      } catch (_e) { /* keep local guess */ }
    };
    resolve();
    return () => { cancelled = true; };
  }, [orderData?.id]);

  // Handle Review Tax Sheet action — opens modal (or auto-submits for external portal)
  const handleReviewTaxSheet = useCallback(async () => {
    if (isExternalPortalOrder) {
      // For external portal, mark as reviewed directly (no modal)
      try {
        const token = localStorage.getItem('token');
        const response = await fetch(
          `${API}/orders/${orderData.id}/tax-sheet-review`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
          }
        );
        if (response.ok) {
          const result = await response.json();
          setTaxSheetReview({
            status: result.review_status,
            reviewed_by: result.reviewed_by,
            reviewed_by_name: result.reviewed_by_name,
            reviewed_at: result.reviewed_at
          });
          toast.success('Tax sheet reviewed and acknowledged');
        } else {
          const errorData = await response.json().catch(() => ({}));
          toast.error(errorData.detail || 'Failed to mark tax sheet as reviewed');
        }
      } catch (error) {
        console.error('Error reviewing tax sheet:', error);
        toast.error('Network error. Please try again.');
      }
      return;
    }
    setReviewModalOpen(true);
  }, [isExternalPortalOrder, orderData?.id]);

  // Callback when review is completed inside the modal
  const handleReviewComplete = useCallback((reviewData) => {
    setTaxSheetReview(reviewData);
  }, []);
  
  // Mail-Away modal state - lifted to FulfillmentTab level for proper rendering
  const [showMailAwayModal, setShowMailAwayModal] = useState(false);
  // MAILAWAY-RETURN-001: reason-only "Return to Mail-Away" when an ACTIVE request already exists
  const [returnMailAway, setReturnMailAway] = useState({ open: false, requests: [], reason: '' });
  const [returnMailAwaySubmitting, setReturnMailAwaySubmitting] = useState(false);
  // MAILAWAY-PENDING-GUARD-001: Admin/TL confirmation before Pending with an ACTIVE request
  const [pendingOverride, setPendingOverride] = useState({ open: false, active: [], args: null });
  const orderDataRef = useRef(orderData);
  orderDataRef.current = orderData;
  // Soft duplicate-cycle ack modal (Feb 27, 2026)
  // Opens when /mailaway/send returns 409 MAILAWAY_CYCLE_ACK_REQUIRED.
  // Stores the original formData so we can retry with cycle_acknowledged: true.
  const [cycleAckModalState, setCycleAckModalState] = useState({
    open: false,
    formData: null,
    pendingCycles: [],
  });
  const [mailAwayTargetJurisdiction, setMailAwayTargetJurisdiction] = useState(null);
  // Feb 2026 — Option C: order-level "Send Mail-Away for All" button.
  // When true, the MailAwayModal loads ALL jurisdictions on the cert
  // and pre-filters them to "not yet sent" (current-tax row absent or
  // inactive). When false, the modal scopes per `targetJurisdiction`.
  const [mailAwayPreselectUnsentOnly, setMailAwayPreselectUnsentOnly] = useState(false);
  const [clientCodeConfig, setClientCodeConfig] = useState(null);
  
  // Fetch client code config when needed for Mail-Away
  useEffect(() => {
    const fetchClientCodeConfig = async () => {
      if (!orderData?.client_code) return;
      // RBAC-403-FIX (Aug 2026): the config only drives Mail-Away features.
      // Search-processor roles are denied /api/client-codes/* by RBAC and
      // never use Mail-Away — skip the fetch instead of logging a 403.
      const _r = (user?.role || '').toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_');
      if (_r === 'search_processor' || _r === 'title_search_processor') {
        setClientCodeConfig(null);
        return;
      }
      try {
        const token = localStorage.getItem('token');
        // dedupedGet collapses the duplicate lookups fired by re-renders.
        const response = await dedupedGet(`${API}/client-codes/${orderData.client_code}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setClientCodeConfig(response.data);
      } catch (err) {
        // 404 = no config exists for this client code — expected empty state.
        // 403 = role not permitted (restricted personas) — also expected.
        if (err.response?.status !== 404 && err.response?.status !== 403) {
          console.error('Failed to fetch client code config:', err);
        }
        setClientCodeConfig(null);
      }
    };
    fetchClientCodeConfig();
  }, [orderData?.client_code]);
  
  // Handle Mail-Away submission — jurisdiction-scoped
  // The modal already filters to the target jurisdiction; formData.jurisdiction_shipping
  // contains ONLY the jurisdiction(s) the user is submitting for.
  //
  // Feb 2026 (Option C): accepts `resumeJurisdictionIds` so the green
  // Resume modal can re-call this handler with the resume flag set,
  // and routes per-result `suggested_action` payloads to the correct
  // follow-up modal/toast instead of dumping a "Use Resend" toast.
  const handleMailAwaySubmit = async (formData, cycleAcknowledged = false, resumeJurisdictionIds = null) => {
    console.log('📬 [MAIL-AWAY] Submitting Mail-Away with data:', formData);
    
    // P1 SAFETY GUARD: Only allow Mail-Away submission from Order Details/Fulfillment page
    // This prevents accidental triggers from dashboard/popup refreshes
    const currentPath = window.location.pathname;
    const isOnOrderDetailsPage = currentPath.includes('/orders/') && currentPath.includes('/fulfillment');
    
    if (!isOnOrderDetailsPage) {
      console.warn('⛔ [MAIL-AWAY] Blocked - not on order details page. Current path:', currentPath);
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      
      // Use the jurisdiction IDs from the modal's formData (already scoped to target jurisdiction)
      const jurisdictionsToSend = (formData.jurisdiction_shipping || []).map(js => js.jurisdiction_id);
      
      console.log('📬 [MAIL-AWAY] Jurisdictions to send (from modal):', jurisdictionsToSend);
      
      if (jurisdictionsToSend.length === 0) {
        toast.error('No jurisdictions selected for Mail-Away.');
        setShowMailAwayModal(false);
        return;
      }
      
      // Build request data using formData for shipping details
      const requestData = {
        jurisdiction_ids: jurisdictionsToSend,
        tax_office_fee: formData.tax_office_fee,
        notes: formData.notes || null,
        jurisdiction_shipping: formData.jurisdiction_shipping || null,
        shipment_charges: formData.shipment_charges,
        shipping_method: formData.shipping_method || null,
        // Soft duplicate-cycle ack (Feb 27, 2026) — true on retry after
        // the operator confirms via the CycleAckModal.
        cycle_acknowledged: cycleAcknowledged,
        // Feb 2026 — Collector defaults snapshot (immutable). Frozen at
        // modal submit time. Server persists onto mail_away_requests so
        // historical billing/audit reports remain stable.
        collector_defaults_snapshot: formData.collector_defaults_snapshot || null,
        // Feb 2026 — Resume after Clarification (Option C):
        // - `order_previous_status` lets the backend's resume gate detect
        //   the Clarification round-trip even when the cycle_trigger
        //   heuristic misses (no clarification status_note timestamped
        //   strictly after the previous Mail-Away).
        // - `resume_jurisdiction_ids` lets the operator confirm Resume
        //   via the green CycleAckModal Resume variant.
        order_previous_status:
          orderData?.previous_status ||
          orderData?.status ||
          null,
        resume_jurisdiction_ids: resumeJurisdictionIds || null,
        // Feb 2026 — PA Delinquent-Grouping (Slice 2). The MailAwayModal
        // sets `delinquent_groups` on the payload when the operator
        // leaves the in-modal banner toggle on Combine. When null/empty
        // the legacy 1-per-jurisdiction route behaviour is unchanged.
        delinquent_groups: formData.delinquent_groups || null,
        // Feb 2026 — Unified Mail-Away selection. The modal sends the
        // explicit subset of jurisdictions that should receive a
        // Current-Tax MLC. When omitted (legacy callers) the backend
        // treats every entry in `jurisdiction_ids` as Current-Tax.
        current_tax_jurisdiction_ids:
          formData.current_tax_jurisdiction_ids !== undefined
            ? formData.current_tax_jurisdiction_ids
            : null,
        // Feb 2026 — Per-request shipping overrides. The modal builds
        // one entry per actual request being created (Current-Tax for
        // each jurisdiction + Delinquent for each Tax Claim Bureau
        // letter, combined or separate). Backend route falls back to
        // `jurisdiction_shipping` when this is null/empty, preserving
        // legacy behaviour.
        per_request_shipping_overrides:
          formData.per_request_shipping_overrides !== undefined
            ? formData.per_request_shipping_overrides
            : null,
      };
      
      console.log('📬 [MAIL-AWAY] Sending request:', requestData);
      
      const response = await fetch(
        `${API}/orders/${orderData.id}/mailaway/send`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestData)
        }
      );
      
      if (response.ok) {
        const result = await response.json();
        console.log('📬 [MAIL-AWAY] Response:', result);
        
        // Check if any requests were actually sent.
        // Feb 2026 P0 (False-Failure-Toast Fix) — the backend may stamp
        // a successful row with one of several "created_*" action labels
        // (`created`, `created_combined`, `created_combined_delinquent`,
        // `created_single_delinquent`) or `updated`/`resumed`. The
        // earlier check only counted exact `'created'` + `'updated'`,
        // so a Combined-Delinquent-only or Combined-Current-only batch
        // hit the "All failed" branch and surfaced a misleading
        // "Mail-Away request failed" toast even when the email actually
        // went out. Count every `r.success === true` row as a successful
        // dispatch instead, and reserve the failure toast for the
        // truly-all-blocked case.
        const successCount = result.results?.filter(r => r.success).length || 0;
        const createdCount = result.results?.filter(r => r.success && r.action === 'created').length || 0;
        const updatedCount = result.results?.filter(r => r.success && r.action === 'updated').length || 0;
        const blockedCount = result.results?.filter(r => !r.success).length || 0;
        
        if (successCount === 0 && blockedCount > 0) {
          // All failed - route based on the structured suggested_action
          // (Option C) so we open the right modal instead of toasting a
          // misleading "Use Resend" message.
          const firstBlocked = result.results?.find(r => !r.success) || {};
          const suggested = firstBlocked.suggested_action;

          // Resume → re-open the CycleAckModal in its green Resume variant.
          if (suggested === 'resume') {
            setShowMailAwayModal(false);
            setCycleAckModalState({
              open: true,
              formData,
              pendingCycles: [{
                jurisdiction_id: firstBlocked.jurisdiction_id,
                jurisdiction_name: firstBlocked.jurisdiction_name,
                cycle_index: 2,
                trigger: 'post_clarification',
                previous_request_id: firstBlocked.previous_request_id,
                previous_status: firstBlocked.previous_request_status,
                resume_eligible: true,
                resume_reason: 'match_after_clarification',
                resume_diff_fields: [],
              }],
            });
            return;
          }

          // Cycle 2 (signature changed) → open yellow Cycle 2 modal.
          if (suggested === 'cycle2') {
            setShowMailAwayModal(false);
            setCycleAckModalState({
              open: true,
              formData,
              pendingCycles: [{
                jurisdiction_id: firstBlocked.jurisdiction_id,
                jurisdiction_name: firstBlocked.jurisdiction_name,
                cycle_index: 2,
                trigger: 'manual_resend',
                previous_request_id: firstBlocked.previous_request_id,
                previous_status: firstBlocked.previous_request_status,
                resume_eligible: false,
                resume_reason: 'shipment_signature_changed',
                resume_diff_fields: firstBlocked.resume_diff_fields || [],
              }],
            });
            return;
          }

          // Resend / Confirm Sent → guide the user to the right inline
          // row action inside the MultiJurisdictionMailAway modal.
          if (suggested === 'resend') {
            toast.info('Mail-Away already confirmed sent', {
              description: firstBlocked.error,
              duration: 7000,
            });
          } else if (suggested === 'confirm_sent') {
            toast.warning('Confirm Sent required', {
              description: firstBlocked.error,
              duration: 7000,
            });
          } else {
            // Unclassified blocker — surface the raw error.
            toast.error(firstBlocked.error || 'Mail-Away request failed');
          }
          setShowMailAwayModal(false);
          return;
        }
        
        // Show success toast. Resume path emits `resumed: true` with
        // no_new_charges so we surface a distinct "Resumed — no new charge"
        // toast instead of the generic created/updated copy.
        const resumedCount = result.results?.filter(r => r.success && r.resumed).length || 0;
        if (resumedCount > 0) {
          toast.success('Mail-Away resumed', {
            description: 'Resumed existing Mail-Away — no new charge.',
            duration: 4000,
          });
        } else if (createdCount > 0 && updatedCount > 0) {
          toast.success(`Mail-Away: ${createdCount} created, ${updatedCount} updated`);
        } else if (updatedCount > 0) {
          toast.success(`Mail-Away request${updatedCount > 1 ? 's' : ''} updated with new parcels`);
        } else if (createdCount > 0) {
          toast.success(`Mail-Away request${createdCount > 1 ? 's' : ''} created successfully`);
        } else if (successCount > 0) {
          // Combined-Current and Combined-Delinquent rows surface as
          // `created_combined*` / `created_single_delinquent` and slip
          // past the `created` filter above. Issue an accurate success
          // toast so the operator never sees a silent / generic message.
          toast.success(
            `Mail-Away request${successCount > 1 ? 's' : ''} sent successfully`
          );
        } else {
          toast.success('Mail-Away request processed');
        }
        
        // Reset state
        setShowMailAwayModal(false);
        setWorkflowSelectedStatus(null);
        
        // GLOBAL RULE: Redirect to dashboard after successful Mail-Away
        // This matches FinalizeOrderCard behavior
        const rolesWithDashboardRedirect = [
          'admin', 
          'team_leader', 
          'tax_search_team_leader', 
          'title_search_team_leader',
          'processor',
          'tax_processor', 
          'title_processor',
          'search_processor',
          'title_search_processor'
        ];
        
        const userRole = user?.role?.toLowerCase() || '';
        const shouldRedirect = rolesWithDashboardRedirect.includes(userRole);
        const isOnFulfillmentPage = currentPath.includes('/orders/') && currentPath.includes('/fulfillment');
        
        if (shouldRedirect && isOnFulfillmentPage) {
          console.log('📬 [MAIL-AWAY] Redirecting to dashboard in 1.5 seconds...');
          setTimeout(() => {
            console.log('📬 [MAIL-AWAY] Executing redirect to dashboard NOW');
            // Feb 27, 2026 — role-aware dashboard (processors → /dashboard/processor-orders)
            navigate(getRoleDashboardPath(user), { replace: true });
          }, 1500);
        } else {
          console.log('📬 [MAIL-AWAY] Mail-Away status set. No redirect.');
        }
        
      } else {
        const errorData = await response.json().catch(() => ({}));
        console.error('📬 [MAIL-AWAY] Server error:', errorData);

        // Soft duplicate-cycle gate (Feb 27, 2026) — backend returns 409
        // with structured detail when a jurisdiction would land on
        // cycle ≥ 2. Open the ack modal instead of toasting an error.
        const detail = errorData?.detail;
        if (
          response.status === 409 &&
          detail &&
          typeof detail === 'object' &&
          detail.error === 'MAILAWAY_CYCLE_ACK_REQUIRED'
        ) {
          setShowMailAwayModal(false);
          setCycleAckModalState({
            open: true,
            formData,
            pendingCycles: detail.pending_cycles || [],
          });
          return;
        }

        setShowMailAwayModal(false);
        // Coerce object details safely so the user never sees "[object Object]".
        const errMsg =
          typeof detail === 'string'
            ? detail
            : detail?.message || errorData?.message || 'Failed to create Mail-Away request';
        throw new Error(errMsg);
      }
    } catch (err) {
      console.error('Failed to create Mail-Away request:', err);
      toast.error(err.message || 'Failed to create Mail-Away request');
    }
  };
  
  // Note: Reason templates are stored in constants file (reasonTemplates.js), not fetched from API
  // The StatusReasonField component handles template loading internally via getTemplatesForStatus()
  
  // Scroll to top when Fulfillment tab is opened
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  // Check if Tax Certificate panel should be shown
  // OD-DUAL-ROLE-001: a processor role HELD as an additional role (users.role_ids) counts too
  // Order Desk primary: processor tools only on orders assigned to them (mirrors the backend own-order rule).
  const isProcessor = ['processor', 'tax_processor', 'search_processor', 'title_search_processor'].some((k) => holdsRole(user, k))
    && (user?.role !== 'order_desk' || orderData?.assignee_id === user?.id);
  const isAdmin = user?.role === 'admin';
  const isTaxTeamLeader = user?.role === 'tax_search_team_leader' || user?.role === 'title_search_team_leader' || user?.role === 'team_leader';
  // DUAL-ROLE-001: a held (secondary) QC Specialist role counts as QC when the order is in QC Review and the
  // user is not its submitter (separation of duties is enforced server-side as well).
  const holdsQcRole = user?.role === 'qc_specialist' || (Array.isArray(user?.role_ids) && user.role_ids.includes('qc_specialist'));
  const actingAsQc = holdsQcRole && orderData?.status === 'qc_review' && orderData?.qc_submitted_by !== user?.id;
  const isQCSpecialist = user?.role === 'qc_specialist' || actingAsQc;
  const canAddQcDocument = actingAsQc && (Array.isArray(user?.permissions) ? user.permissions.some((p) => p.resource === 'orders' && p.action === 'qc_add_document') : true);
  // Accounting users opening orders from Billing Dashboard get a strict
  // read-only view: they SEE the same panels as processors (so they can
  // verify the work behind an invoice) but cannot mutate anything.
  // Backend RBAC already rejects mutation calls from this role (see
  // require_role([...]) lists on PATCH /orders/{id}/status, POST /complete,
  // POST /files/upload, DELETE /files/{id}); this flag drives the UI.
  const isAccounting = user?.role === 'accounting';
  // TYPING-001 (Aug 2026): Typing Processors get strict READ-ONLY access to
  // the completed Search work (Research Log) — view only, never edit.
  const isTypingProcessor = user?.role === 'typing_processor';
  const canSeeTaxPanel = (isProcessor || isAdmin || isTaxTeamLeader || isQCSpecialist || isAccounting) && isTaxProduct(orderData);
  
  // Phase 1 — Processor Feedback Banner (Return / Reject feedback)
  // Shown when order has open QC feedback (qc_status in returned/rejected).
  // The component itself enforces role-based visibility via the backend
  // GET /qc-feedback endpoint (returns 403 to unauthorised roles).
  const hasOpenQCFeedback = ['returned', 'rejected'].includes(orderData?.qc_status)
    && !['complete', 'completed'].includes(orderData?.status); // BILLING-QC-GATE-001

  // Determine if panel should be read-only
  // - Complete orders: read-only for everyone except admin
  // - QC Review: read-only for ALL roles (including Admin) - proper QC workflow requires using workflow actions
  //   Admin should use: Send Back to Processor, Unlock Order, or Reassign QC Reviewer
  // - Accounting: ALWAYS read-only regardless of order state
  // - Jun 14, 2026 — QCC-CORR-001: Correction Required orders are also
  //   read-only UNTIL the processor clicks "Acknowledge & Open for Edit".
  //   The backend already gates `submit-for-qc` on the same flag
  //   (server.py L21547) but the UI was not enforcing edit-time
  //   blocking, so users could modify fulfillment fields without ever
  //   touching the acknowledgment banner. We now key off
  //   `qc_feedback_acknowledged_at` to match the intended workflow gate
  //   and record proper lifecycle accountability.
  const isQCReview = orderData?.status === 'qc_review';
  // MLS-COMPONENT-QC-001: a dual order with a component awaiting QC shows the QC command bar (order itself stays unlocked)
  const hasComponentQC = !!qcComponentOf(orderData);
  const isComplete = orderData?.status === 'complete';
  const isPendingCorrection = orderData?.status === 'pending_correction'
    || (['returned', 'rejected'].includes(orderData?.qc_status) && !['complete', 'completed'].includes(orderData?.status));
  // TYPING-RETURN-002: a Typing return is a distinct correction source with
  // its own acknowledgment field. Scope each unacked gate to its own source
  // so a stale QC ack timestamp can't unlock a Typing return (and vice versa).
  const isTypingReturn = orderData?.status === 'pending_correction'
    && orderData?.typing_status === 'returned';
  const isTypingReturnUnacked = isTypingReturn && !orderData?.typing_return_acknowledged_at;
  const isPendingCorrectionUnacked = (isTypingReturn
    ? isTypingReturnUnacked
    : (isPendingCorrection && !orderData?.qc_feedback_acknowledged_at));
  // TYPING-EDIT-001 (Jun 2026): typists edit fulfillment while the order is in
  // the active Typing stage; the resolved delivery method decides field scope
  // inside SearchFulfillmentPanel (Internal → full edit, External → Parcel IDs
  // only). Outside the Typing stage typists remain strictly read-only.
  const isTypingActive = isTypingProcessor && orderData?.status === 'typing'
    && (!orderData?.typing_assigned_to || orderData?.typing_assigned_to === user?.id);
  const isReadOnly = isComplete || isQCReview || isAccounting || isPendingCorrectionUnacked || (isTypingProcessor && !isTypingActive);
  
  // Show QC Review lock message for all non-QC roles (Processors, Team Leaders, Admin)
  // QC Specialists see the form in read-only mode but with different messaging
  const showQCReviewLockMessage = isQCReview && !isQCSpecialist;
  const showQCSpecialistMessage = (isQCReview || hasComponentQC) && isQCSpecialist;

  // Check if this is a Tax Search / Update Tax Search order
  // These products render inside TaxCertificatePanel and don't need the Executive Header
  // MLS is intentionally NOT included — MLS uses the Executive Header for order context
  const isTaxSearchOrder = orderData?.client_product === 'Tax Search' || 
                           orderData?.client_product === 'Update Tax Search';

  // Determine if Search Fulfillment sidebar layout should be used
  // BILLING-QC-GATE-001 — workflow for the explicit QC override action
  const qcOverrideWorkflow = (orderData?.product_type_normalized === 'tax' || isTaxSearchOrder
    || /tax|mls|hoa|utility|municipal/i.test(orderData?.client_product || '')) ? 'tax' : 'title';
  const qcOverrideButton = (
    <QcOverrideCompleteButton
      orderId={orderData?.id}
      orderNumber={orderData?.order_number}
      workflow={qcOverrideWorkflow}
      userRole={user?.role}
      orderStatus={orderData?.status}
      qcStatus={orderData?.qc_status}
      qcIteration={orderData?.qc_iteration}
      onCompleted={(patch) => { onOrderUpdate?.(patch); onRefreshOrder?.(); }}
      className="mt-3"
    />
  );
  const showAdminOverrideOutsideQcBanner = user?.role === 'admin' && orderData?.status !== 'qc_review'
    && ['returned', 'rejected', 'cancelled', 'resubmission_required'].includes(orderData?.qc_status)
    && !['complete', 'completed', 'cancelled', 'typing'].includes(orderData?.status);

  const showSearchPanel = !canSeeTaxPanel && !isTaxSearchOrder && (isProcessor || isAdmin || isTaxTeamLeader || isQCSpecialist || isAccounting || isTypingProcessor);

  // Feb 26, 2026 (UAT-MAILAWAY-GROUPING-PRECREATE) — pre-creation
  // grouping eligibility for the Fulfillment-tab "Mail-Away
  // (All Jurisdictions)" banner. Mirrors the PA-grouping rules used
  // inside the Send Mail-Away modal so processors get an
  // immediate visual cue at the top of the page when the order
  // qualifies — instead of having to navigate into a jurisdiction's
  // MLC & Workflow tab to discover that grouping is possible.
  //
  // Feb 26, 2026 follow-up — the canonical Tax-Certificate
  // parcels/jurisdictions are NOT on `orderData` (the
  // GET /api/orders/:id/details response intentionally omits the
  // draft tree). They live in `sharedCertificateData` which is
  // hydrated by `<TaxCertificatePanel>` on mount via the cert-draft
  // API. The first version of this memo only walked `orderData` and
  // returned false for every PA order. We now read jurisdictions
  // from sharedCertificateData (primary) AND orderData (fallback,
  // for sub-products that don't render a cert panel), and we ALSO
  // build the order shape passed to `computeDelinquentGroups` from
  // whichever source the jurisdictions actually came from so the
  // helper's `_isPA(order)` rule still trips.
  const mailAwayGroupingEligible = useMemo(() => {
    if (!orderData) return false;
    const jurisdictions = [];
    const seen = new Set();
    const pushJ = (j) => {
      const id = j?.jurisdiction_id || j?.id;
      if (id && !seen.has(String(id))) {
        seen.add(String(id));
        jurisdictions.push(j);
      }
    };
    // Primary source: hydrated cert state from TaxCertificatePanel.
    for (const p of sharedCertificateData?.parcels || []) {
      for (const j of p?.jurisdictions || []) pushJ(j);
    }
    // Fallback paths — keep them so non-cert order types still work.
    for (const p of orderData?.parcels || []) {
      for (const j of p?.jurisdictions || []) pushJ(j);
    }
    const cert = orderData?.tax_certificate || orderData?.certificate || {};
    for (const p of cert?.parcels || []) {
      for (const j of p?.jurisdictions || []) pushJ(j);
    }
    for (const j of cert?.jurisdictions || []) pushJ(j);
    for (const j of orderData?.jurisdictions || []) pushJ(j);
    if (jurisdictions.length < 2) return false;

    // Build a shape the grouping helper understands. It wants
    // order.property_state plus an order.parcels[].jurisdictions[] (or
    // order.jurisdictions[]) tree to walk; we hand it ALL collected
    // jurisdictions in one synthetic parcel so the helper finds them.
    const orderShape = {
      property_state:
        orderData?.property_state
        || orderData?.property?.state
        || orderData?.property?.property_state,
      parcels: [{ jurisdictions }],
    };
    const ids = jurisdictions.map((j) => String(j.jurisdiction_id || j.id));
    return computeDelinquentGroups(orderShape, ids) !== null;
  }, [orderData, sharedCertificateData]);

  // PERF: Memoize onComplete callback to stabilize workflowActions reference
  const handleComplete = useCallback(async (overrideFlag) => {
    // COMPLETE-BTN-FEEDBACK-001: ref-based in-flight guard (state updates are async)
    if (completeInFlightRef.current) return;
    completeInFlightRef.current = true;
    console.log('[COMPLETE] Starting order completion...');
    setCompleteError(null);
    setWorkflowIsSubmitting(true);
    try {
    // ========================================================================
    // PRE-FLIGHT VALIDATION — block completion if required fields are missing.
    // This is the frontend guard for the Complete button in JurisdictionManager.
    // The backend also hard-blocks, but we catch it here for a better UX.
    // ========================================================================
    if (sharedCertificateData) {
      const preflight = [];
      const parcels = Array.isArray(sharedCertificateData.parcels) ? sharedCertificateData.parcels : [];
      
      // Jun 2, 2026 — Cross-parcel Information Source coverage.
      // Mirror of the backend `_is_source_covered_by_other_parcel` helper
      // and the FE `isSourceCoveredByAnotherParcel` in JurisdictionManager.
      // When the SAME jurisdiction (name + type, case-insensitive) already
      // has a source filled on a DIFFERENT parcel, the current parcel is
      // considered covered — matches the "Source already captured from
      // Parcel N" banner so the Complete button cannot disagree with it.
      const isSourceCoveredByOtherParcel = (jurName, jurType, currentIdx) => {
        if (!jurName || !Array.isArray(parcels) || parcels.length <= 1) return false;
        const name = (jurName || '').toLowerCase().trim();
        const type = (jurType || '').toLowerCase().trim();
        if (!name) return false;
        return parcels.some((p, pIdx) => {
          if (pIdx === currentIdx || !p) return false;
          return (Array.isArray(p.jurisdictions) ? p.jurisdictions : []).some(j => {
            const jn = (j?.jurisdiction_name || '').toLowerCase().trim();
            const jt = (j?.jurisdiction_type || '').toLowerCase().trim();
            return jn === name && jt === type && j?.source;
          });
        });
      };
      
      parcels.forEach((parcel, pIdx) => {
        const pLabel = parcels.length > 1 ? `Parcel ${pIdx + 1}` : '';
        const pPrefix = pLabel ? `${pLabel}: ` : '';
        
        // Parcel/APN required — coerce safely (string | array | number | null)
        const rawApn = parcel?.parcel_apn;
        const apn = Array.isArray(rawApn)
          ? rawApn.filter((v) => v != null).map(String).join(', ')
          : String(rawApn ?? '');
        if (!apn.trim()) {
          preflight.push(`${pPrefix}Parcel/APN is required`);
        }
        
        // Information Source required on every jurisdiction
        // (unless covered by the same jurisdiction on another parcel)
        (Array.isArray(parcel?.jurisdictions) ? parcel.jurisdictions : []).forEach((jur) => {
          if (!jur) return;
          if (!jur.source && !isSourceCoveredByOtherParcel(jur.jurisdiction_name, jur.jurisdiction_type, pIdx)) {
            const jName = jur.jurisdiction_name || 'Unknown jurisdiction';
            preflight.push(`${pPrefix}${jName}: Information Source is required — open the "MLC & Workflow" tab to set it`);
          }
        });
      });
      
      // ORDER-COMPLETION-VALIDATION-001: supporting documents + per-source evidence are mandatory
      // (Internal and External alike). Backend enforces the same rule; this gives immediate feedback.
      // TL-COMPLETION-DOC-GATE-001 (Oct 2026): read via the same endpoint the Files tab uses (allowed for every
      // role). A non-OK response is NOT "no files" — the backend stays the authoritative gate.
      try {
        const _tk = localStorage.getItem('token');
        const _fr = await fetch(`${API}/orders/${orderData.id}/files`, { headers: { Authorization: `Bearer ${_tk}` } });
        if (!_fr.ok) throw new Error(`files lookup failed (${_fr.status})`);
        const _fl = await _fr.json();
        const _activeFiles = (Array.isArray(_fl) ? _fl : (_fl.files || [])).filter(f => f.is_active !== false);
        if (_activeFiles.length === 0) {
          preflight.push('At least one supporting document is required — upload it under the Files tab or the Evidence panel before completing');
        }
        const EVIDENCE_LABEL = { online: 'portal screenshot / document', calling: 'call recording', calling_online: 'call recording + screenshot', email: 'email evidence', fax: 'fax document', mail: 'mail evidence', other: 'supporting document' };
        parcels.forEach((parcel, pIdx) => {
          const pPrefix = parcels.length > 1 ? `Parcel ${pIdx + 1}: ` : '';
          (Array.isArray(parcel?.jurisdictions) ? parcel.jurisdictions : []).forEach((jur) => {
            if (jur?.source && !jur.source_evidence && _activeFiles.length === 0) {
              preflight.push(`${pPrefix}${jur.jurisdiction_name || 'Unknown jurisdiction'}: ${EVIDENCE_LABEL[jur.source] || 'evidence'} required for source "${jur.source}"`);
            }
          });
        });
      } catch (_e) { /* backend remains the authoritative gate */ }
      
      if (preflight.length > 0) {
        setCompleteError({ title: 'Cannot complete order', lines: preflight });
        toast.error(
          <div>
            <strong>Cannot complete order:</strong>
            <ul style={{ margin: '4px 0 0 0', paddingLeft: '16px' }}>
              {preflight.slice(0, 5).map((msg, idx) => (
                <li key={idx} style={{ fontSize: '12px' }}>{msg}</li>
              ))}
              {preflight.length > 5 && (
                <li style={{ fontSize: '12px', fontStyle: 'italic' }}>+{preflight.length - 5} more issues</li>
              )}
            </ul>
          </div>,
          { duration: 8000 }
        );
        return;
      }
    }
    
      const token = localStorage.getItem('token');
      const response = await fetch(
        `${API}/orders/${orderData.id}/complete`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({ completion_notes: overrideFlag || null })
        }
      );
      if (response.ok) {
        const result = await response.json();
        const isQC = result.delivery_status === 'qc_review';
        console.log('[COMPLETE] Order completion succeeded', { delivery_status: result.delivery_status });
        // Jun 2026 — show a real success POPUP instead of a 2s toast that
        // died mid-navigation. The dialog auto-redirects after 4s (or
        // immediately on button click) via handleCompletionRedirect.
        setCompletionSuccess({
          orderNumber: orderData?.order_number || '',
          qcReview: isQC,
        });
        // Jun 2026 — UI cleanup: no extra success toast for standard
        // completion. The "Order Completed!" dialog above is the single
        // authoritative confirmation (users reported the duplicate toast
        // as UI noise). The QC variant keeps its toast because it carries
        // routing info that must survive the dashboard redirect.
        if (isQC) {
          toast.success('Order submitted for QC review. A QC Specialist will review and complete the order.', { duration: 6000 });
        }
      } else {
        const errorData = await response.json().catch(() => ({}));
        const detail = errorData.detail;
        // COMPLETE-BTN-FEEDBACK-001: keep the real backend reason (string, structured
        // {message}, or FastAPI validation list) and persist it inline under the button.
        let message;
        if (Array.isArray(detail)) {
          message = detail.map((d) => d?.msg || (typeof d === 'string' ? d : '')).filter(Boolean).join('; ');
        } else if (detail && typeof detail === 'object') {
          message = detail.message || detail.detail || detail.error;
        } else if (typeof detail === 'string') {
          message = detail;
        }
        if (!message) {
          message = response.status >= 500
            ? `Completion failed on the server (HTTP ${response.status}). Please try again or contact an Admin.`
            : `Failed to complete order (HTTP ${response.status}). Please check all required fields.`;
        }
        setCompleteError({ title: `Completion blocked (HTTP ${response.status})`, lines: message.split('\n').filter(Boolean) });
        toast.error(message, { duration: 8000 });
      }
    } catch (error) {
      console.error('Error completing order:', error);
      const message = error?.name === 'TypeError' && /fetch/i.test(String(error?.message || ''))
        ? 'Network error — the completion request did not reach the server. Please try again.'
        : `Unexpected error while completing: ${error?.message || String(error)}`;
      setCompleteError({ title: 'Completion failed', lines: [message] });
      toast.error(message, { duration: 8000 });
    } finally {
      completeInFlightRef.current = false;
      setWorkflowIsSubmitting(false);
    }
  }, [orderData?.id, orderData?.order_number, navigate, sharedCertificateData, user]);

  // Jun 2026 — redirect after the completion success popup. Role-aware
  // dashboard path (processors → /dashboard/processor-orders, accounting →
  // /admin/billing, ...). `replace: true` keeps the completed order out of
  // back history. Guarded so the auto-redirect timer and the button click
  // can't double-navigate.
  const handleCompletionRedirect = useCallback(() => {
    if (completionRedirectRef.current) return;
    completionRedirectRef.current = true;
    navigate(getRoleDashboardPath(user), { replace: true });
  }, [navigate, user]);

  useEffect(() => {
    if (!completionSuccess) return;
    const timer = setTimeout(handleCompletionRedirect, 4000);
    return () => clearTimeout(timer);
  }, [completionSuccess, handleCompletionRedirect]);

  // PERF: Memoize onSubmitStatus callback
  const handleSubmitStatus = useCallback(async (status, reason, jurisdictionContext, selectedChipIds, extra = {}) => {
    // MAILAWAY-FOLLOWUP-001: follow-up on the ACTIVE request — status stays Mail-Away
    if (status === 'mailaway_followup') {
      setWorkflowIsSubmitting(true);
      try {
        const token = localStorage.getItem('token');
        const resp = await axios.post(
          `${API}/orders/${orderDataRef.current.id}/mailaway/follow-up`,
          { note: reason || '', chips: Array.isArray(selectedChipIds) ? selectedChipIds : [], source: 'ui' },
          { headers: { Authorization: `Bearer ${token}` } }
        );
        toast.success('Follow-up added', { description: 'Status stays Mail-Away — no new charge.', duration: 3000 });
        setWorkflowSelectedStatus(null);
        setWorkflowReason('');
        setWorkflowEditMode(false);
        if (onOrderUpdate && resp?.data?.note) {
          onOrderUpdate({ ...orderDataRef.current, status_notes: [...(orderDataRef.current.status_notes || []), resp.data.note] });
        }
      } catch (err) {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : (detail?.message || (Array.isArray(detail) ? detail[0]?.msg : 'Failed to add follow-up'));
        toast.error(msg);
        if (detail?.error === 'ORDER_NOT_IN_MAILAWAY') {
          setReturnMailAway({ open: true, requests: activeMailAwayRequests(orderDataRef.current), reason: reason || '' });
        }
      } finally {
        setWorkflowIsSubmitting(false);
      }
      return;
    }
    // MAILAWAY-PENDING-GUARD-001: Admin/TL must confirm before moving an order with an ACTIVE request to Pending
    if (status === 'pending' && !extra.overrideActiveMailAway) {
      const cur = orderDataRef.current;
      const active = activeMailAwayRequests(cur);
      if (active.length && cur?.status !== 'pending') {
        setPendingOverride({ open: true, active, args: [status, reason, jurisdictionContext, selectedChipIds] });
        return;
      }
    }
    if (status === 'mailaway') {
      // MAILAWAY-RETURN-001: Mail-Away is the workflow state; "Create Mail-Away Request" is an action.
      // With an ACTIVE request on file (not response_received/cancelled), offer Return instead of Create.
      const current = orderDataRef.current;
      const active = activeMailAwayRequests(current);
      if (active.length && current?.status !== 'mail_away') {
        setReturnMailAway({ open: true, requests: active, reason: reason || '' });
        return;
      }
      setMailAwayTargetJurisdiction(jurisdictionContext || null);
      setShowMailAwayModal(true);
      return;
    }
    setWorkflowIsSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      // QC-VAL-POPUP-001: never send an empty reason string — the backend
      // enforces min_length=10 and an empty '' triggered a confusing 422.
      // TAX-PENDING-EMAIL-001: forward structured chip ids so backend automations
      // (Fax Sent, Tax Collector email) key off the chip marker, not the text.
      const resp = await axios.patch(
        `${API}/orders/${orderData.id}/status`,
        {
          status,
          reason: reason && reason.trim() ? reason : undefined,
          ...(Array.isArray(selectedChipIds) && selectedChipIds.length ? { selected_chip_ids: selectedChipIds } : {}),
          // TAX-EMAIL-CONFIRM-001: scope of the Email action = the jurisdiction the processor is working on
          ...(jurisdictionContext && Array.isArray(selectedChipIds) && selectedChipIds.length ? { email_jurisdiction: jurisdictionContext } : {}),
          ...(extra.overrideActiveMailAway ? { override_active_mailaway: true } : {}),
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const statusLabel = status === 'pending' ? 'Pending' : status === 'clarification' ? 'Clarification' : status === 'qc_review' ? 'QC Review' : status;
      toast.success(`Marked as ${statusLabel} successfully`, { duration: 2500 });
      showTaxEmailAutomationToasts(resp?.data?.email_automation);
      setWorkflowSelectedStatus(null);
      setWorkflowReason('');
      setWorkflowEditMode(false);
      setWorkflowIsSubmitting(false);
      startSmartRedirect(statusLabel);
    } catch (err) {
      console.error('Failed to update status:', err);
      const detail = err?.response?.data?.detail;
      // QC-VAL-POPUP-001: structured missing-fields error → show a blocking
      // modal listing every required field instead of failing silently.
      if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.error === 'MAILAWAY_ACTIVE_BLOCKS_PENDING') {
        // MAILAWAY-PENDING-GUARD-001: authoritative backend block
        toast.error(detail.message, { duration: 7000 });
      } else if (detail && typeof detail === 'object' && !Array.isArray(detail) && detail.error_code === 'qc_validation_failed') {
        setQcValidationModal({
          message: detail.message || 'Required fields are missing.',
          missingFields: Array.isArray(detail.missing_fields) ? detail.missing_fields : [],
        });
      } else if (detail && typeof detail === 'object' && (detail.code === 'parcel_apn_required' || String(detail.code || '').startsWith('tax_email_') || String(detail.code || '').startsWith('email_jurisdiction'))) {
        // TAX-EMAIL-APN-GATE-001 / TAX-EMAIL-CONFIRM-001: authoritative backend gate → exact message, nothing persisted
        toast.error(detail.title ? `${detail.title}: ${detail.message}` : detail.message, { duration: 7000 });
      } else {
        const errMsg = Array.isArray(detail)
          ? (detail[0]?.msg || 'Validation failed. Please check the required fields.')
          : (typeof detail === 'string' ? detail : 'Failed to update status. Please try again.');
        toast.error(errMsg);
      }
      setWorkflowIsSubmitting(false);
    }
  }, [orderData?.id, startSmartRedirect, onOrderUpdate]);

  // MAILAWAY-RETURN-001: POST /mailaway/return — status back to Mail-Away, existing request kept.
  const handleReturnToMailAway = useCallback(async (reasonText) => {
    setReturnMailAwaySubmitting(true);
    try {
      const token = localStorage.getItem('token');
      const resp = await axios.post(
        `${API}/orders/${orderDataRef.current.id}/mailaway/return`,
        { reason: reasonText, source: 'ui' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Returned to Mail-Away', { description: resp?.data?.message || 'Existing request kept — no new charge.', duration: 3500 });
      setReturnMailAway({ open: false, requests: [], reason: '' });
      setWorkflowSelectedStatus(null);
      setWorkflowReason('');
      setWorkflowEditMode(false);
      // MAILAWAY-RECOVERY-002: stay on the order — optimistic status flip, then authoritative refetch
      if (onOrderUpdate) onOrderUpdate({ status: 'mail_away', status_reason: reasonText });
      if (onRefreshOrder) await onRefreshOrder();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : (Array.isArray(detail) ? detail[0]?.msg : 'Failed to return to Mail-Away'));
    } finally {
      setReturnMailAwaySubmitting(false);
    }
  }, [onOrderUpdate, onRefreshOrder]);

  // MAILAWAY-RECOVERY-001: every "create Mail-Away" entry point routes to Return first while an ACTIVE
  // request exists and the order is not in Mail-Away — prevents an accidental Cycle 2.
  const openMailAwayCreate = useCallback((jurisdiction = null, preselectUnsentOnly = false) => {
    const cur = orderDataRef.current;
    const active = activeMailAwayRequests(cur);
    if (active.length && cur?.status !== 'mail_away') {
      setReturnMailAway({ open: true, requests: active, reason: '' });
      return;
    }
    setMailAwayTargetJurisdiction(jurisdiction);
    setMailAwayPreselectUnsentOnly(preselectUnsentOnly);
    setShowMailAwayModal(true);
  }, []);
  const handleCopyToUpdateNotes = useCallback((text) => {
    setWorkflowReason((prev) => {
      if (!prev || !prev.trim()) return text;
      return prev + '\n\n' + text;
    });
  }, []);

  // PERF: Memoize workflowActions to prevent TaxCertificatePanel re-renders on unrelated state changes
  // For Accounting role: pass null so TaxCertificatePanel hides ALL workflow buttons
  // (Mail Away, Clarification, Pending submission, Complete). Backend RBAC enforces this too.
  const memoizedWorkflowActions = useMemo(() => {
    if (isAccounting) return null;
    const hasParcelAPN = !!(
      sharedCertificateData?.parcel_apn ||
      sharedCertificateData?.parcel_apns ||
      sharedCertificateData?.parcels?.[0]?.parcel_apn ||
      orderData?.tax_certificate?.parcel_apn ||
      orderData?.tax_certificate?.parcel_apns ||
      orderData?.tax_certificate?.parcels?.[0]?.parcel_apn ||
      orderData?.property?.parcel_id ||
      orderData?.property?.parcel_apn ||
      orderData?.parcel_apn ||
      orderData?.parcel_apns
    );

    // Order-level data only — per-jurisdiction contact fields (fax, phone, email)
    // are resolved by JurisdictionManager from the ACTIVE jurisdiction, not hardcoded to [0]
    const jurisdictionData = {
      county: orderData?.tax_certificate?.county || sharedCertificateData?.county || '',
      parcel_apn: orderData?.tax_certificate?.parcel_apn || orderData?.property?.parcel_apn || '',
      owner_name: orderData?.borrower_owner_name || orderData?.borrower?.borrower_name || '',
    };

    return {
      selectedStatus: workflowSelectedStatus,
      onStatusSelect: setWorkflowSelectedStatus,
      reason: workflowReason,
      onReasonChange: setWorkflowReason,
      editMode: workflowEditMode,
      setEditMode: setWorkflowEditMode,
      isSubmitting: workflowIsSubmitting,
      isTaxProduct: isTaxProduct(orderData),
      statusNotes: orderData?.status_notes || [],
      hasParcelAPN,
      jurisdictionData,
      onComplete: handleComplete,
      completeError,
      clearCompleteError,
      taxSheetReview: taxSheetReview,
      onReviewTaxSheet: handleReviewTaxSheet,
      isAdmin: isAdmin,
      onSubmitStatus: handleSubmitStatus,
      // MAILAWAY-PENDING-GUARD-001 / MAILAWAY-FOLLOWUP-001
      activeMailAway: activeMailAwayRequests(orderData),
      canOverridePending: ['admin', 'tax_search_team_leader', 'title_search_team_leader'].includes((user?.role || '').toLowerCase()),
      // Enriched orderData for fax APN validation: merge certificate parcel data
      // since APN may live in tax_certificate_drafts rather than the order document
      orderData: {
        ...orderData,
        parcel_apns: orderData?.parcel_apns || sharedCertificateData?.parcel_apns || null,
        parcels: (orderData?.parcels?.length ? orderData.parcels : null)
          || sharedCertificateData?.parcels || [],
        property: orderData?.property || {},
        tax_certificate: {
          ...(orderData?.tax_certificate || {}),
          parcel_apns: orderData?.tax_certificate?.parcel_apns || sharedCertificateData?.parcel_apns || null,
          parcels: orderData?.tax_certificate?.parcels || sharedCertificateData?.parcels || []
        }
      },
      userRole: user?.role?.toLowerCase() || ''
    };
  }, [
    workflowSelectedStatus, workflowReason, workflowEditMode, workflowIsSubmitting,
    orderData, sharedCertificateData, taxSheetReview, handleReviewTaxSheet,
    isAdmin, handleComplete, handleSubmitStatus, user?.role, isAccounting,
    completeError, clearCompleteError
  ]);

  return (
    <div className="od-wide py-4">
      <div className="space-y-6">
        {/* Sentinel for sticky detection */}
        <div ref={stickySentinelRef} className="h-0 w-0" aria-hidden="true" />

        {/* Executive Header (Top Property/Order Summary Card) — REMOVED globally.
            Per product decision (May 26, 2026), this card duplicated info
            already available in the page header (order # / product / client)
            and in the Parcel header strip below the Tax Certificate badge.
            It is now hidden for BOTH External Portal and Internal Only
            delivery methods. If we ever need it back, restore the JSX
            block from git history (see commit removing data-testid
            "fulfillment-sticky-summary"). */}

        {/* Accounting Read-Only Banner — strict view-only mode for accounting role.
            Backend RBAC enforces this via require_role([...]) allow-lists on every
            mutation endpoint; this banner sets user expectations clearly. */}
        {isAccounting && (
          <div
            className="bg-indigo-50 border border-indigo-200 rounded-xl p-4 flex items-start gap-3"
            data-testid="accounting-readonly-banner"
          >
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-indigo-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-indigo-800">Read-Only View — Accounting</h3>
              <p className="text-sm text-indigo-700 mt-1">
                You can review every detail of this order, but editing, file uploads, status
                changes, and workflow actions are disabled for the Accounting role.
              </p>
            </div>
          </div>
        )}

        {/* QC-RULE-001 — order left QC for correction: must be resubmitted and approved before completion */}
        {['resubmission_required', 'cancelled'].includes(orderData?.qc_status)
          && !['complete', 'completed', 'cancelled', 'qc_review', 'typing'].includes(orderData?.status) && (
          <div className="rounded-lg border border-orange-200 bg-orange-50/70 px-4 py-3 text-sm" data-testid="qc-resubmission-required-banner">
            <p className="font-medium text-orange-900">QC approval required before completion</p>
            <p className="text-orange-800 mt-0.5">
              This order entered QC and has not been approved{orderData?.qc_status === 'resubmission_required' ? ' (it was sent back for correction)' : ''}.
              Correct the file, then use <strong>QC Review</strong> to resubmit it. Completion and delivery are blocked until QC approves.
            </p>
            {showAdminOverrideOutsideQcBanner && qcOverrideButton}
          </div>
        )}

        {/* BILLING-QC-GATE-001 — explicit terminal state: QC cycle closed without approval (admin exception) */}
        {orderData?.qc_status === 'bypassed' && (
          <div className="flex items-center gap-2 text-xs" data-testid="qc-bypassed-chip"
            title={`Reason: ${orderData?.qc_bypass_reason || '—'}${orderData?.qc_override_reason ? ` — ${orderData.qc_override_reason}` : ''}${orderData?.qc_bypassed_by_name ? ` (by ${orderData.qc_bypassed_by_name})` : ''}`}>
            <span className="inline-flex items-center px-2 py-0.5 rounded-full border border-orange-300 bg-orange-50 text-orange-800 font-medium">QC bypassed — not a QC approval</span>
            <span className="text-slate-500">{(orderData?.qc_bypass_reason || '').replace(/_/g, ' ')}{orderData?.qc_bypassed_by_name ? ` · ${orderData.qc_bypassed_by_name}` : ''}</span>
          </div>
        )}

        {/* QC Review Lock Message for Processors and Team Leaders */}
        {showQCReviewLockMessage && !isAdmin && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3" data-testid="qc-review-lock-banner">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-amber-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-amber-800">Order Under QC Review</h3>
              <p className="text-sm text-amber-700 mt-1">
                This order is currently under QC Review and is locked for editing. A QC Specialist will review and complete the order.
              </p>
              {qcOverrideButton}
            </div>
          </div>
        )}
        
        {/* Phase 1 — Processor Feedback Banner (E2): visible to submitter / QC / TL / Admin when order has open QC feedback.
            Jun 14, 2026 — QCC-CORR-001: wired `onAcknowledged` so the parent
            re-fetches the order, flipping `qc_feedback_acknowledged_at` from
            null → ISO timestamp; this unlocks the fulfillment form
            immediately after the user clicks "Acknowledge & Open for Edit". */}
        {/* QC-CORR-MODAL-001: premium blocking modal + compact reminder; the admin
            emergency override now lives inside the modal's "Admin Actions" section
            (same component / permission / confirmation / audit). */}
        {hasOpenQCFeedback && (
          <ProcessorFeedbackBanner
            orderId={orderData.id}
            orderNumber={orderData.order_number}
            currentUser={user}
            onAcknowledged={onOrderUpdate}
            adminActions={showAdminOverrideOutsideQcBanner && ['returned', 'rejected'].includes(orderData?.qc_status)
              ? <div data-testid="qc-admin-override-returned">{qcOverrideButton}</div>
              : null}
          />
        )}

        {/* TYPING-RETURN-002 — "Returned from Typing" banner. Distinct from QC
            feedback (own endpoint/fields); acknowledging unlocks Fulfillment. */}
        {orderData?.status === 'pending_correction' && orderData?.typing_status === 'returned' && (
          <TypingReturnBanner
            orderId={orderData.id}
            currentUser={user}
            onAcknowledged={onOrderUpdate}
          />
        )}

        {/* QC Review Lock Message for Admin - with workflow guidance */}
        {showQCReviewLockMessage && isAdmin && (
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3" data-testid="qc-review-admin-banner">
            <div className="flex-shrink-0 mt-0.5">
              <svg className="w-5 h-5 text-blue-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
              </svg>
            </div>
            <div>
              <h3 className="text-sm font-semibold text-blue-800">Order Under QC Review</h3>
              <p className="text-sm text-blue-700 mt-1">
                This order is locked during QC Review to maintain audit integrity. Use workflow actions to manage this order:
              </p>
              <ul className="text-sm text-blue-700 mt-2 ml-4 list-disc">
                <li>Send Back to Processor</li>
                <li>Reassign QC Reviewer</li>
                <li>Emergency override (exceptional) — reason required, recorded as QC bypassed, not a QC approval</li>
              </ul>
              {qcOverrideButton}
            </div>
          </div>
        )}
        
        {/* QC-REVIEW-UI-COMPACT-002 (Oct 2026) — compact QC command bar + Timeline & Notes drawer.
            Submitter notes / Typing return reason now live in the drawer (Notes tab). */}
        {showQCSpecialistMessage && (
          <QCReviewBar
            orderData={orderData}
            user={user}
            onOrderUpdate={onOrderUpdate}
            formatRelativeAgo={formatRelativeAgo}
            formatDateTime={formatDateTime}
            showPreviewPdf={isTaxProduct(orderData)}
            canAddDocument={isSearchProduct(orderData) && canAddQcDocument}
          />
        )}

        {/* Tax Certificate Panel */}
        {canSeeTaxPanel && (
          <>
            {/* MAILAWAY-PREMIUM-UI-003: the Return-to-Mail-Away banner is shown in Order Summary only (duplicate removed here) */}
            {/* Feb 2026 — Option C (Fulfillment-tab placement). Order-level
                "Send Mail-Away for All" action strip. Rendered above the
                Tax Certificate Panel so processors don't have to leave
                the Fulfillment screen to launch a combined send. The
                button opens the existing MailAwayModal with
                `preselectUnsentOnly=true` and `targetJurisdiction=null`,
                which causes the modal to (a) load every jurisdiction on
                the cert, (b) pre-filter out any with an active
                current-tax Mail-Away row, and (c) surface the PA
                Delinquent-Grouping banner above the submit footer when
                ≥2 selected jurisdictions share a Tax Claim Bureau.

                Feb 2026 (UAT-MAILAWAY-CONDITIONAL-BANNER) — only
                surface this banner once at least one Mail-Away request
                exists on the order. Pre-creation the banner was just
                visual noise that implied a request already existed.
                The first Mail-Away is initiated via the per-
                jurisdiction "Send Mail-Away" actions in the Tax
                Certificate panel; from the second send onward the
                banner here becomes the convenient batch entry point.

                Feb 26, 2026 (UAT-MAILAWAY-GROUPING-PRECREATE) —
                EXTENDED to also surface the banner BEFORE the first
                send when the order qualifies for PA delinquent
                grouping (`mailAwayGroupingEligible` above mirrors the
                3-rule eligibility used inside the modal). Without
                this, a processor had no top-of-page cue that an
                order could be sent as one combined Mail-Away.
                Copy + analytics ids adapt to the pre-creation path
                so they don't lie about there being existing requests.
            */}
            {(() => {
              // MLS-UI-CLEANUP (Aug 2026): this banner is the TAX
              // jurisdiction/MLC Mail-Away entry point. On dual MLS orders it
              // stays for the Tax Search workspace but is hidden while the MLS
              // workspace is active (MLS has its own dedicated Mail-Away
              // section). MLS component rows never trigger it either.
              const taxMailAwayRequests = (orderData?.mail_away_requests || [])
                .filter((r) => (r?.component || 'tax') !== 'mls');
              const hasExistingRequests = taxMailAwayRequests.length > 0;
              const mlsWorkspaceActive = isMlsDualOrder && effectiveMlsWsTab === 'mls';
              const showBanner = !isReadOnly
                && !mlsWorkspaceActive
                && (isProcessor || isAdmin || isTaxTeamLeader)
                && (hasExistingRequests || mailAwayGroupingEligible);
              if (!showBanner) return null;
              const headline = hasExistingRequests
                ? 'Mail-Away Requests'
                : 'Mail-Away Eligible — Combine This Order';
              const subtext = hasExistingRequests
                ? 'Pick exactly which Current-Tax and Delinquent letters to send per jurisdiction. Multiple jurisdictions sharing a Tax Claim Bureau can combine into one delinquent letter.'
                : 'This order has multiple jurisdictions sharing a Tax Claim Bureau and can be sent as one combined Mail-Away. Eligible jurisdictions are pre-selected and the combine toggle is on by default.';
              const buttonTitle = hasExistingRequests
                ? "Opens the Mail-Away modal scoped to every jurisdiction on this order. Each jurisdiction's Current-Tax and Delinquent (Tax Claim Bureau) options can be toggled independently before submit."
                : 'Opens the Mail-Away modal with every eligible jurisdiction pre-selected and the PA delinquent-grouping toggle enabled. Review the per-jurisdiction toggles before sending.';
              return (
                <div
                  className="flex items-center justify-between gap-3 bg-blue-50/60 border border-blue-200 rounded-xl px-4 py-3"
                  data-testid="fulfillment-mailaway-action-strip"
                  data-banner-mode={hasExistingRequests ? 'existing-requests' : 'grouping-eligible'}
                >
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="p-2 bg-blue-100 rounded-lg flex-shrink-0">
                      <Mail className="w-5 h-5 text-blue-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="text-sm font-semibold text-blue-900">{headline}</h3>
                      <p className="text-xs text-blue-700/80 truncate">
                        {subtext}
                      </p>
                    </div>
                  </div>
                  <Button
                    type="button"
                    variant="default"
                    size="sm"
                    onClick={() => openMailAwayCreate(null, true)}
                    data-testid="fulfillment-send-mailaway-for-all-btn"
                    className="gap-1.5 flex-shrink-0"
                    title={buttonTitle}
                  >
                    <Send className="w-4 h-4" />
                    Mail-Away (All Jurisdictions)
                  </Button>
                </div>
              );
            })()}

            {/* DELIVERY-STATUS-UI-002: Delivery Status panel removed from Fulfillment — delivery info lives in Activity; Resend moved to the order header */}
            <EnhancedCard>
            <TaxCertificatePanel 
              orderData={orderData} 
              readOnly={isReadOnly}
              externalCertificateData={sharedCertificateData}
              onCertificateDataChange={setSharedCertificateData}
              workflowActions={memoizedWorkflowActions}
            />
          </EnhancedCard>
          {/* TAX-EMAIL-UI-SIMPLIFY-001: Tax Collector Email Delivery lives in Order Summary only (removed from Fulfillment) */}
          </>
        )}
        
        {/* Search Fulfillment Panel — internal 2-column layout with lean sidebar */}
        {showSearchPanel && (
          <SearchFulfillmentPanel 
            orderData={orderData}
            readOnly={isReadOnly}
            typistMode={isTypingProcessor}
            currentUser={user}
            onOrderUpdate={onOrderUpdate}
            onRequestSmartRedirect={startSmartRedirect}
          />
        )}
        
        {/* Smart Auto-Redirect Banner — shown after successful Pending/Clarification submission */}
        {pendingRedirect && (
          <div 
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-50 animate-in slide-in-from-bottom-4 fade-in duration-300"
            data-testid="smart-redirect-banner"
          >
            <div className="flex items-center gap-4 bg-slate-900 text-white pl-5 pr-3 py-3 rounded-xl shadow-2xl border border-slate-700">
              <CheckCircle className="w-5 h-5 text-emerald-400 flex-shrink-0" />
              <span className="text-sm font-medium">
                Redirecting to dashboard in {pendingRedirect.countdown}s...
              </span>
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  cancelRedirect();
                  toast.info('Redirect cancelled — staying on page', { duration: 1500 });
                }}
                className="ml-1 px-3 py-1 text-xs font-semibold text-slate-900 bg-white hover:bg-slate-100 rounded-lg transition-colors"
                data-testid="cancel-redirect-btn"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Mail-Away Modal - rendered outside hidden div */}
      <MailAwayModal
        isOpen={showMailAwayModal}
        onClose={() => {
          setShowMailAwayModal(false);
          setMailAwayTargetJurisdiction(null);
          setMailAwayPreselectUnsentOnly(false);
          setWorkflowSelectedStatus(null);
        }}
        onSubmit={handleMailAwaySubmit}
        orderData={orderData}
        certificateData={sharedCertificateData}
        clientCodeConfig={clientCodeConfig}
        targetJurisdiction={mailAwayTargetJurisdiction}
        preselectUnsentOnly={mailAwayPreselectUnsentOnly}
      />

      {/* MAILAWAY-PENDING-GUARD-001: Admin/TL override confirmation */}
      <PendingOverrideDialog
        state={pendingOverride}
        onClose={() => setPendingOverride({ open: false, active: [], args: null })}
        onConfirm={() => { const a = pendingOverride.args; setPendingOverride({ open: false, active: [], args: null }); handleSubmitStatus(a[0], a[1], a[2], a[3], { overrideActiveMailAway: true }); }}
      />
      {/* MAILAWAY-RETURN-001: reason-only return to an existing active Mail-Away request */}
      <ReturnToMailAwayDialog
        open={returnMailAway.open}
        onOpenChange={(o) => setReturnMailAway((s) => ({ ...s, open: o }))}
        requests={returnMailAway.requests}
        initialReason={returnMailAway.reason}
        isSubmitting={returnMailAwaySubmitting}
        onConfirm={handleReturnToMailAway}
        onCreateNew={() => { setReturnMailAway({ open: false, requests: [], reason: '' }); setMailAwayTargetJurisdiction(null); setShowMailAwayModal(true); }}
      />
      {/* Soft duplicate-cycle ack modal (Feb 27, 2026). Opened only
          when the backend returns 409 MAILAWAY_CYCLE_ACK_REQUIRED on
          a Mail-Away send. On confirm, the original formData is
          re-submitted with cycle_acknowledged: true. */}
      {cycleAckModalState.open && (
        <CycleAckModal
          jurisdiction={{
            id: cycleAckModalState.pendingCycles[0]?.jurisdiction_id,
            name: cycleAckModalState.pendingCycles[0]?.jurisdiction_name,
          }}
          notes={cycleAckModalState.formData?.notes}
          pendingCycles={cycleAckModalState.pendingCycles}
          loading={false}
          onConfirm={async ({ resume } = {}) => {
            // Honor the modal's variant: Resume variant calls
            // onConfirm({ resume: true }) → re-submit with the
            // jurisdiction id in resume_jurisdiction_ids (no new
            // charges). Cycle 2 variant calls onConfirm({ resume:
            // false }) → re-submit with cycle_acknowledged=true.
            const fd = cycleAckModalState.formData;
            const jurId = cycleAckModalState.pendingCycles[0]?.jurisdiction_id;
            setCycleAckModalState({ open: false, formData: null, pendingCycles: [] });
            if (resume && jurId) {
              await handleMailAwaySubmit(fd, false, [jurId]);
            } else {
              await handleMailAwaySubmit(fd, true);
            }
          }}
          onClose={() =>
            setCycleAckModalState({ open: false, formData: null, pendingCycles: [] })
          }
        />
      )}

      {/* ═══════════ Tax Sheet Review Modal ═══════════ */}
      <ReviewTaxCertModal
        open={reviewModalOpen}
        onOpenChange={setReviewModalOpen}
        orderId={orderData?.id}
        onReviewComplete={handleReviewComplete}
      />

      {/* ═══════════ Completion Success Popup (Jun 2026) ═══════════ */}
      {completionSuccess && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
          data-testid="completion-success-dialog"
          role="alertdialog"
          aria-labelledby="completion-success-title"
          aria-describedby="completion-success-desc"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 text-center animate-in fade-in zoom-in-95 duration-300">
            <div className="mx-auto w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mb-4">
              <svg className="w-9 h-9 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <h2 id="completion-success-title" className="text-xl font-semibold text-slate-900 mb-1" data-testid="completion-success-title">
              {completionSuccess.qcReview ? 'Submitted for QC Review' : 'Order Completed!'}
            </h2>
            {completionSuccess.orderNumber && (
              <p className="text-sm font-medium text-slate-700 mb-2" data-testid="completion-success-order-number">
                {completionSuccess.orderNumber}
              </p>
            )}
            <p id="completion-success-desc" className="text-sm text-slate-500 mb-6">
              {completionSuccess.qcReview
                ? 'A QC Specialist will review and finalize this order.'
                : 'The tax certificate has been finalized and the order is now marked complete.'}
              {' '}Redirecting to your dashboard…
            </p>
            <Button
              type="button"
              onClick={handleCompletionRedirect}
              className="w-full bg-green-600 hover:bg-green-700 text-white"
              data-testid="completion-success-dashboard-btn"
            >
              Go to Dashboard Now
            </Button>
          </div>
        </div>
      )}

      {/* ═══════════ QC Review Missing-Fields Popup (QC-VAL-POPUP-001) ═══════════ */}
      {qcValidationModal && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-900/50 backdrop-blur-sm"
          data-testid="qc-validation-modal"
          role="alertdialog"
          aria-labelledby="qc-validation-title"
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full mx-4 p-8 animate-in fade-in zoom-in-95 duration-300">
            <div className="mx-auto w-16 h-16 rounded-full bg-amber-100 flex items-center justify-center mb-4">
              <AlertCircle className="w-9 h-9 text-amber-600" />
            </div>
            <h2 id="qc-validation-title" className="text-xl font-semibold text-slate-900 mb-1 text-center" data-testid="qc-validation-modal-title">
              Cannot Submit for QC Review
            </h2>
            <p className="text-sm text-slate-500 mb-4 text-center" data-testid="qc-validation-modal-message">
              {qcValidationModal.message}
            </p>
            {qcValidationModal.missingFields.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 max-h-64 overflow-y-auto">
                <ul className="space-y-1.5" data-testid="qc-validation-missing-list">
                  {qcValidationModal.missingFields.map((field, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-amber-800" data-testid={`qc-validation-missing-item-${idx}`}>
                      <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                      <span>{field}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div className="flex gap-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setQcValidationModal(null)}
                className="flex-1"
                data-testid="qc-validation-close-btn"
              >
                Close
              </Button>
              <Button
                type="button"
                onClick={() => {
                  setQcValidationModal(null);
                  window.scrollTo({ top: 0, behavior: 'smooth' });
                }}
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="qc-validation-review-btn"
              >
                Review Missing Fields
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ═══════════ Call Notes Scratchpad ═══════════ */}
      {/* TYPING-NOTES-001: typists get a read-only view of the Search Processor's saved notes
          (GET only on the backend); every other role keeps the editable scratchpad unchanged */}
      <CallNotesScratchpad
        orderId={orderData?.id}
        onCopyToUpdateNotes={handleCopyToUpdateNotes}
        readOnly={isTypingProcessor}
      />
    </div>
  );
};

const ActivityTab = ({ orderData }) => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalEvents, setTotalEvents] = useState(0);
  const [expandedEvents, setExpandedEvents] = useState(new Set());
  const [error, setError] = useState(null);
  
  const eventsPerPage = 20;
  
  // Fetch activity events from backend
  const fetchActivityEvents = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Construct API URL with proper fallback for both development and production
      const API_URL = process.env.REACT_APP_BACKEND_URL || '';
      const baseUrl = API_URL ? `${API_URL}/api` : '/api';
      const token = localStorage.getItem('token');
      
      if (!orderData?.id) {
        throw new Error('Order ID is missing');
      }
      
      const params = new URLSearchParams({
        page: page.toString(),
        limit: eventsPerPage.toString()
      });
      
      const fullUrl = `${baseUrl}/orders/${orderData.id}/activity?${params}`;
      
      const response = await fetch(fullUrl, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setEvents(data.events || []);
        setTotalEvents(data.total || 0);
        
        // Track telemetry for activity tab views
        if (window.gtag) {
          window.gtag('event', 'activity_tab_view', {
            event_category: 'order_management',
            event_label: `page_${page}`,
            order_id: orderData.id
          });
        }
      } else {
        // Try to get error message from response
        let errorMsg = `HTTP ${response.status}`;
        try {
          const errorData = await response.json();
          errorMsg = errorData.detail || errorMsg;
        } catch (e) {
          // Response wasn't JSON - likely an HTML error page
          console.error('Received non-JSON response, possible routing error');
        }
        throw new Error(`Failed to fetch activity events: ${errorMsg}`);
      }
      
    } catch (error) {
      console.error('Error fetching activity events:', error);
      setError(error.message);
      setEvents([]);
      setTotalEvents(0);
    } finally {
      setLoading(false);
    }
  };
  
  // Effect to fetch events when dependencies change
  useEffect(() => {
    fetchActivityEvents();
  }, [orderData.id, page]);
  
  // Helper functions
  const getEventIcon = (eventType) => {
    switch (eventType) {
      case 'status.change': return <CheckCircle className="w-4 h-4" />;
      case 'status.clarification_reopen': return <RotateCcw className="w-4 h-4 text-purple-600" />;
      case 'status.response_received': return <CheckCircle className="w-4 h-4 text-green-600" />;
      case 'status.mail_away_received': return <Mail className="w-4 h-4 text-orange-600" />;
      case 'status.clarification_received': return <CheckCircle className="w-4 h-4 text-purple-600" />;
      case 'status.send_back': return <AlertCircle className="w-4 h-4 text-orange-600" />;
      case 'tax_cert.started': return <FileText className="w-4 h-4 text-green-600" />;
      case 'tax_cert.edit': return <FileText className="w-4 h-4" />;
      case 'tax_cert.completed': return <CheckCircle className="w-4 h-4 text-blue-600" />;
      case 'mailaway.request_created': return <Mail className="w-4 h-4 text-orange-600" />;
      // Feb 2026 — Activity Timeline differentiation for Mail-Away.
      case 'mailaway.resumed_after_clarification': return <Mail className="w-4 h-4 text-emerald-600" />;
      case 'mailaway.updated': return <Mail className="w-4 h-4 text-amber-600" />;
      case 'file.post_completion_upload': return <Upload className="w-4 h-4 text-amber-600" />;
      case 'file.upload': 
      case 'file.replace':
      case 'file.delete': return <Upload className="w-4 h-4" />;
      case 'assignment.change': return <User className="w-4 h-4" />;
      case 'auto_link.event': return <Link className="w-4 h-4" />;
      case 'delinquent.workflow': return <AlertCircle className="w-4 h-4" />;
      // Billing events
      case 'billing.price_override': return <DollarSign className="w-4 h-4 text-amber-600" />;
      case 'billing.charge_added': return <DollarSign className="w-4 h-4 text-green-600" />;
      case 'billing.charge_removed': return <DollarSign className="w-4 h-4 text-red-600" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };
  
  // Premium event color coding
  const getEventColor = (eventType) => {
    switch (eventType) {
      case 'work_session.started': return { dot: 'bg-emerald-500', ring: 'ring-emerald-100', glow: 'shadow-emerald-200' };
      case 'tax_cert.started': return { dot: 'bg-emerald-500', ring: 'ring-emerald-100', glow: 'shadow-emerald-200' };
      case 'tax_cert.edit': return { dot: 'bg-purple-500', ring: 'ring-purple-100', glow: 'shadow-purple-200' };
      case 'order.edit': return { dot: 'bg-purple-500', ring: 'ring-purple-100', glow: 'shadow-purple-200' };
      case 'tax_cert.completed': return { dot: 'bg-indigo-600', ring: 'ring-indigo-100', glow: 'shadow-indigo-200' };
      case 'mailaway.request_created': return { dot: 'bg-orange-500', ring: 'ring-orange-100', glow: 'shadow-orange-200' };
      // Feb 2026 — distinct dots for Resume (green) and Revised (amber)
      // Mail-Away events on the Activity Timeline.
      case 'mailaway.resumed_after_clarification': return { dot: 'bg-emerald-500', ring: 'ring-emerald-100', glow: 'shadow-emerald-200' };
      case 'mailaway.updated': return { dot: 'bg-amber-500', ring: 'ring-amber-100', glow: 'shadow-amber-200' };
      case 'status.change': return { dot: 'bg-blue-500', ring: 'ring-blue-100', glow: 'shadow-blue-200' };
      case 'status.clarification_reopen': return { dot: 'bg-purple-500', ring: 'ring-purple-100', glow: 'shadow-purple-200' };
      case 'status.response_received': return { dot: 'bg-green-500', ring: 'ring-green-100', glow: 'shadow-green-200' };
      case 'status.mail_away_received': return { dot: 'bg-orange-500', ring: 'ring-orange-100', glow: 'shadow-orange-200' };
      case 'status.clarification_received': return { dot: 'bg-purple-500', ring: 'ring-purple-100', glow: 'shadow-purple-200' };
      case 'status.send_back': return { dot: 'bg-orange-500', ring: 'ring-orange-100', glow: 'shadow-orange-200' };
      case 'file.upload': return { dot: 'bg-green-500', ring: 'ring-green-100', glow: 'shadow-green-200' };
      case 'file.post_completion_upload': return { dot: 'bg-amber-500', ring: 'ring-amber-100', glow: 'shadow-amber-200' };
      case 'autosave': return { dot: 'bg-sky-400', ring: 'ring-sky-100', glow: 'shadow-sky-200' };
      // Billing events - amber/yellow for revenue-related
      case 'billing.price_override': return { dot: 'bg-amber-500', ring: 'ring-amber-100', glow: 'shadow-amber-200' };
      case 'billing.charge_added': return { dot: 'bg-emerald-500', ring: 'ring-emerald-100', glow: 'shadow-emerald-200' };
      case 'billing.charge_removed': return { dot: 'bg-red-500', ring: 'ring-red-100', glow: 'shadow-red-200' };
      default: return { dot: 'bg-slate-400', ring: 'ring-slate-100', glow: 'shadow-slate-200' };
    }
  };
  
  // Get role-specific badge color
  const getRoleBadgeColor = (role) => {
    const roleStr = role?.toLowerCase() || '';
    if (roleStr.includes('admin')) return 'bg-purple-100 text-purple-700 border-purple-200';
    if (roleStr.includes('leader') || roleStr.includes('team_lead')) return 'bg-blue-100 text-blue-700 border-blue-200';
    if (roleStr.includes('processor')) return 'bg-slate-100 text-slate-700 border-slate-200';
    if (roleStr.includes('order_desk')) return 'bg-amber-100 text-amber-700 border-amber-200';
    return 'bg-gray-100 text-gray-700 border-gray-200';
  };
  
  // Format role name for display
  // Phase A.7.10 (Feb 2026) — when the actor role is missing OR is the
  // legacy "system" placeholder (left behind by older backend code paths),
  // surface a meaningful label instead of the misleading default "User".
  // This makes audit attribution honest:
  //   • real user → properly titled role badge (e.g. "Tax Search Team Leader")
  //   • automated / unknown → "Automated" badge
  const formatRoleName = (role) => {
    const normalized = (role || '').toString().trim().toLowerCase();
    if (!normalized || normalized === 'system') return 'Automated';
    return role.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
  };

  // Get user initials for avatar
  const getUserInitials = (name) => {
    if (!name || name === 'System') return 'AP';  // "Automated Process"
    const parts = name.trim().split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };
  
  const toggleEventExpansion = (eventId) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };
  
  // Helper: Check if a value looks like a UUID
  const isUUID = (value) => {
    if (!value || typeof value !== 'string') return false;
    return /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value);
  };
  
  // Helper: Check if value is multi-line (like address)
  const isMultiLineValue = (value) => {
    if (!value || typeof value !== 'string') return false;
    return value.includes('\n') || value.length > 80;
  };
  
  // Helper: Check if value represents "empty" or "removed"
  const isEmptyValue = (value) => {
    if (value === null || value === undefined) return true;
    if (typeof value === 'string') {
      const v = value.trim().toLowerCase();
      return v === '' || v === '—' || v === '-' || v === 'null' || v === 'none' || v === 'n/a';
    }
    return false;
  };
  
  // Helper: Get change type for color coding
  const getChangeType = (oldValue, newValue) => {
    const oldEmpty = isEmptyValue(oldValue);
    const newEmpty = isEmptyValue(newValue);
    
    if (oldEmpty && !newEmpty) return 'added';      // Green - Added
    if (!oldEmpty && newEmpty) return 'removed';    // Red - Removed
    return 'modified';                               // Blue - Modified
  };
  
  // Helper: Get color classes based on change type
  const getChangeColors = (changeType) => {
    switch (changeType) {
      case 'added':
        return {
          badge: 'bg-emerald-50 text-emerald-700 border-emerald-200',
          icon: 'text-emerald-500',
          label: 'Added'
        };
      case 'removed':
        return {
          badge: 'bg-red-50 text-red-700 border-red-200',
          icon: 'text-red-500',
          label: 'Removed'
        };
      default: // modified
        return {
          badge: 'bg-blue-50 text-blue-700 border-blue-200',
          icon: 'text-blue-500',
          label: 'Modified'
        };
    }
  };
  
  // Helper: Format display value - handle UUIDs, empty values, etc.
  const formatDisplayValue = (value, fieldName) => {
    if (isEmptyValue(value)) {
      return '(empty)';
    }
    
    // If it's a UUID and we have a display_name in the field, use that instead
    // Otherwise show a readable placeholder
    if (isUUID(value)) {
      return '(ID reference)';
    }
    
    return value;
  };
  
  // Render a single field change with proper formatting
  const renderSingleFieldChange = (field, index) => {
    const changeType = getChangeType(field.old_value, field.new_value);
    const colors = getChangeColors(changeType);
    const isMultiLineOld = isMultiLineValue(field.old_value);
    const isMultiLineNew = isMultiLineValue(field.new_value);
    const isMultiLine = isMultiLineOld || isMultiLineNew;
    
    // Format values for display
    const oldDisplay = formatDisplayValue(field.old_value, field.field_name);
    const newDisplay = formatDisplayValue(field.new_value, field.field_name);
    
    return (
      <div 
        key={index} 
        className="bg-white rounded-lg border border-slate-200 p-3 hover:border-slate-300 transition-colors"
      >
        {/* Field Name Header */}
        <div className="flex items-center justify-between mb-2">
          <span className="font-semibold text-slate-800 text-sm">{field.display_name}</span>
          <span className={`text-xs px-2 py-0.5 rounded-full ${colors.badge} font-medium`}>
            {colors.label}
          </span>
        </div>
        
        {/* Value Changes */}
        {isMultiLine ? (
          // Multi-line layout for long text (addresses, notes, etc.)
          <div className="space-y-2">
            {/* Old Value */}
            {!isEmptyValue(field.old_value) && (
              <div className="text-xs">
                <div className="text-slate-500 mb-1 font-medium">Previous:</div>
                <div className="bg-slate-50 border border-slate-200 rounded-md p-2 text-slate-600 whitespace-pre-wrap font-mono text-xs">
                  {oldDisplay}
                </div>
              </div>
            )}
            {/* New Value */}
            {!isEmptyValue(field.new_value) && (
              <div className="text-xs">
                <div className="text-slate-500 mb-1 font-medium">New:</div>
                <div className={`border rounded-md p-2 whitespace-pre-wrap font-mono text-xs ${
                  changeType === 'added' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' :
                  changeType === 'removed' ? 'bg-red-50 border-red-200 text-red-800' :
                  'bg-blue-50 border-blue-200 text-blue-800'
                }`}>
                  {newDisplay}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Inline layout for short values
          <div className="flex items-center gap-2 flex-wrap text-sm">
            {/* Old Value */}
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
              isEmptyValue(field.old_value) 
                ? 'bg-slate-100 text-slate-500 border border-slate-200 italic'
                : 'bg-slate-100 text-slate-700 border border-slate-200'
            }`}>
              {oldDisplay}
            </span>
            
            {/* Arrow */}
            <svg className={`w-4 h-4 ${colors.icon} flex-shrink-0`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
            </svg>
            
            {/* New Value */}
            <span className={`inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium ${
              isEmptyValue(field.new_value)
                ? 'bg-slate-100 text-slate-500 border border-slate-200 italic'
                : colors.badge
            }`}>
              {newDisplay}
            </span>
          </div>
        )}
      </div>
    );
  };
  
  // Premium field changes renderer with audit-grade formatting
  const renderFieldChanges = (event) => {
    const details = event.details;
    const changedFields = details.changed_fields || [];
    
    if (changedFields.length === 0) return null;
    
    const isExpanded = expandedEvents.has(event.id);
    const fieldsToShow = isExpanded ? changedFields : changedFields.slice(0, 3);
    const hasMore = changedFields.length > 3;
    
    return (
      <div className="mt-3 bg-gradient-to-br from-slate-50 to-slate-100/50 rounded-lg p-4 border border-slate-200/60">
        {/* Summary header for multi-field changes */}
        {changedFields.length > 1 && (
          <div className="flex items-center gap-2 mb-3 pb-2 border-b border-slate-200">
            <svg className="w-4 h-4 text-slate-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <span className="text-sm font-medium text-slate-700">
              {changedFields.length} field{changedFields.length !== 1 ? 's' : ''} updated
            </span>
          </div>
        )}
        
        {/* Field changes list */}
        <div className="space-y-3">
          {fieldsToShow.map((field, index) => renderSingleFieldChange(field, index))}
        </div>
        
        {/* Expand/Collapse button */}
        {hasMore && (
          <div className="mt-3 pt-3 border-t border-slate-200/60">
            <button
              onClick={() => toggleEventExpansion(event.id)}
              className="text-xs font-semibold text-purple-600 hover:text-purple-700 flex items-center gap-1 transition-colors"
            >
              {isExpanded ? (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                  Show less
                </>
              ) : (
                <>
                  <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                  View {changedFields.length - 3} more field{changedFields.length - 3 !== 1 ? 's' : ''}
                </>
              )}
            </button>
          </div>
        )}
      </div>
    );
  };
  
  const totalPages = Math.ceil(totalEvents / eventsPerPage);
  
  return (
    <div className="od-wide py-4">
      <div className="bg-white rounded-2xl border border-slate-200 p-5" style={{ boxShadow: '0 10px 40px rgba(15,23,42,0.08)' }}>
        <div className="border-b border-slate-200 pb-4 mb-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-200">
              <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-bold text-slate-900">Activity Timeline</h2>
              <p className="text-[13px] text-slate-600 mt-0.5">Complete history of all changes and updates</p>
            </div>
          </div>
        </div>
        
        {/* Loading State */}
        {loading && (
          <div className="text-center py-12">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
            <p className="text-sm text-gray-600">Loading activity...</p>
          </div>
        )}
        
        {/* Error State */}
        {error && (
          <div className="text-center py-12">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-50 rounded-full flex items-center justify-center">
              <AlertCircle className="w-8 h-8 text-red-400" />
            </div>
            <h3 className="text-sm font-medium text-gray-900 mb-2">Error loading activity</h3>
            <p className="text-sm text-gray-500 mb-4">{error}</p>
            <Button 
              variant="outline" 
              size="sm"
              onClick={fetchActivityEvents}
              className="text-blue-600 border-blue-200 hover:bg-blue-50"
            >
              Try Again
            </Button>
          </div>
        )}
        
        {/* Premium Events Timeline */}
        {!loading && !error && events.length > 0 && (
          <div className="relative">
            {/* Vertical Timeline Line */}
            <div className="absolute left-4 top-0 bottom-0 w-px bg-gradient-to-b from-slate-200 via-slate-300 to-slate-200"></div>
            
            <div className="space-y-3">
              {events.map((event, index) => {
                const colors = getEventColor(event.type);
                return (
                  <div key={event.id} className="relative flex items-start gap-4">
                    {/* Premium Timeline Dot with Glow */}
                    <div className="relative z-10 flex-shrink-0">
                      <div className={`w-8 h-8 rounded-full ${colors.dot} ${colors.ring} ring-4 flex items-center justify-center shadow-lg`}>
                        <div className="w-2 h-2 bg-white rounded-full"></div>
                      </div>
                    </div>
                    
                    {/* Premium Activity Card */}
                    <div className="flex-1 min-w-0 bg-white rounded-xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-200 hover:border-slate-300 group">
                      <div className="px-4 py-3">
                        {/* Card Header */}
                        <div className="flex items-start justify-between mb-2 gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              {getEventIcon(event.type)}
                              <h3 className="text-sm font-bold text-slate-900 leading-tight">
                                {event.summary}
                              </h3>
                            </div>
                          </div>
                          <time className="text-xs font-medium text-slate-500 whitespace-nowrap ml-2">
                            {formatDateTime(event.created_at)}
                          </time>
                        </div>
                        
                        {/* Premium Actor Information with Avatar */}
                        <div className="flex items-center gap-3 mb-1">
                          {/* Avatar Circle with Initials */}
                          <div className="w-7 h-7 rounded-full bg-gradient-to-br from-purple-500 to-purple-600 flex items-center justify-center text-white text-xs font-bold shadow-sm">
                            {getUserInitials(event.actor?.name || event.actor_name)}
                          </div>
                          
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="text-sm font-semibold text-slate-800">
                              {event.actor?.name || event.actor_name || 'Automated Process'}
                            </span>
                            
                            {/* Role Badge */}
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium border ${getRoleBadgeColor(event.actor?.role || event.actor_role)}`}>
                              {formatRoleName(event.actor?.role || event.actor_role)}
                            </span>
                            
                            {event.source && (
                              <>
                                <span className="text-xs text-slate-400">•</span>
                                <span className="text-xs text-slate-500 font-medium">{event.source}</span>
                              </>
                            )}
                          </div>
                        </div>
                        
                        {/* Event-specific details with premium styling */}
                        {event.type === 'tax_cert.edit' && renderFieldChanges(event)}
                        {event.type === 'order.edit' && renderFieldChanges(event)}
                        
                        {/* Work Session Started */}
                        {event.type === 'work_session.started' && (
                          <div className="mt-3 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-lg p-4 border border-emerald-200">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="text-sm font-medium text-emerald-800">
                                Started working on this order
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Tax Certificate Started */}
                        {event.type === 'tax_cert.started' && (
                          <div className="mt-3 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-lg p-4 border border-emerald-200">
                            <div className="flex items-center gap-2">
                              <svg className="w-4 h-4 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <p className="text-sm font-medium text-emerald-800">
                                First edit to tax certificate form
                              </p>
                            </div>
                          </div>
                        )}
                        
                        {/* Tax Certificate Completed */}
                        {event.type === 'tax_cert.completed' && event.details.populated_fields_count && (
                          <div className="mt-3 bg-gradient-to-br from-indigo-50 to-indigo-100/50 rounded-lg p-4 border border-indigo-200">
                            <div className="space-y-2">
                              <div className="flex items-center gap-2">
                                <svg className="w-4 h-4 text-indigo-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                                </svg>
                                <p className="text-sm font-semibold text-indigo-800">
                                  Certificate completed with {event.details.populated_fields_count} populated fields
                                </p>
                              </div>
                              {event.details.certificate_filename && (
                                <p className="text-xs text-indigo-700 font-medium ml-6">
                                  📄 {event.details.certificate_filename}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Mail-Away Request */}
                        {event.type === 'mailaway.request_created' && (
                          <div className="mt-3 bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg p-4 border border-orange-200">
                            <div className="space-y-2.5">
                              {event.details.tax_office_fee && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-orange-800">Tax Office Fee</span>
                                  <span className="font-semibold text-orange-900">${event.details.tax_office_fee}</span>
                                </div>
                              )}
                              {event.details.shipment_charges && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-orange-800">Shipment</span>
                                  <span className="font-semibold text-orange-900">${event.details.shipment_charges}</span>
                                </div>
                              )}
                              <div className="flex justify-between items-center text-sm">
                                <span className="font-medium text-orange-800">SASE Required</span>
                                <span className="font-semibold text-orange-900">{event.details.sase_required ? 'Yes' : 'No'}</span>
                              </div>
                              {event.details.pdf_filename && (
                                <p className="text-xs text-orange-700 font-medium pt-2 border-t border-orange-200">
                                  📄 {event.details.pdf_filename}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Feb 2026 — Mail-Away Resume After Clarification.
                            Distinct green card showing the previous cycle
                            and the explicit "No new charges" framing so
                            audit reviews can identify resume actions at a
                            glance. */}
                        {event.type === 'mailaway.resumed_after_clarification' && (
                          <div className="mt-3 bg-gradient-to-br from-emerald-50 to-emerald-100/50 rounded-lg p-4 border border-emerald-300">
                            <div className="flex items-center gap-2 mb-2">
                              <span className="inline-flex items-center px-2 py-0.5 rounded-md bg-emerald-100 border border-emerald-300 text-emerald-800 text-[11px] font-semibold tracking-wide">
                                RESUMED — NO NEW CHARGES
                              </span>
                            </div>
                            <div className="space-y-2 text-sm">
                              {(event.details?.jurisdiction_name || event.payload?.jurisdiction_name) && (
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-emerald-800">Jurisdiction</span>
                                  <span className="font-semibold text-emerald-900">
                                    {event.details?.jurisdiction_name || event.payload?.jurisdiction_name}
                                  </span>
                                </div>
                              )}
                              {(event.details?.previous_cycle_index || event.payload?.previous_cycle_index) && (
                                <div className="flex justify-between items-center">
                                  <span className="font-medium text-emerald-800">Continuing Cycle</span>
                                  <span className="font-semibold text-emerald-900">
                                    Cycle {event.details?.previous_cycle_index || event.payload?.previous_cycle_index}
                                  </span>
                                </div>
                              )}
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-emerald-800">MLC Fee</span>
                                <span className="font-semibold text-emerald-900">$0.00</span>
                              </div>
                              <div className="flex justify-between items-center">
                                <span className="font-medium text-emerald-800">Shipping Fee</span>
                                <span className="font-semibold text-emerald-900">$0.00</span>
                              </div>
                              <div className="pt-2 border-t border-emerald-200 flex justify-between items-center">
                                <span className="font-semibold text-emerald-900">New Charges</span>
                                <span className="font-bold text-emerald-900">$0.00</span>
                              </div>
                              {(event.details?.previous_request_id || event.payload?.previous_request_id) && (
                                <p className="text-[11px] text-emerald-700 pt-1 font-mono">
                                  Continues Request ID:{' '}
                                  {event.details?.previous_request_id || event.payload?.previous_request_id}
                                </p>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Mail-Away Sent Event - Full Cost Breakdown */}
                        {(event.type === 'mailaway.sent' || event.type === 'mailaway.updated') && event.details && (
                          <div className="mt-3 bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg p-4 border border-orange-200">
                            <div className="space-y-2.5">
                              {/* Jurisdiction */}
                              {event.details.jurisdiction_name && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-orange-800">Jurisdiction</span>
                                  <span className="font-semibold text-orange-900">{event.details.jurisdiction_name}</span>
                                </div>
                              )}
                              
                              {/* Delivery Method */}
                              {event.details.delivery_method && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-orange-800">Delivery Method</span>
                                  <span className="font-semibold text-orange-900">{event.details.delivery_method}</span>
                                </div>
                              )}
                              
                              {/* MLC Fee */}
                              {(event.details.mlc_fee !== undefined && event.details.mlc_fee !== null) && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-orange-800">MLC Fee</span>
                                  <span className="font-semibold text-orange-900">${Number(event.details.mlc_fee).toFixed(2)}</span>
                                </div>
                              )}
                              
                              {/* Shipping Fee */}
                              {(event.details.shipping_fee !== undefined && event.details.shipping_fee !== null) && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-orange-800">Shipping Fee</span>
                                  <span className="font-semibold text-orange-900">${Number(event.details.shipping_fee).toFixed(2)}</span>
                                </div>
                              )}
                              
                              {/* Total Cost */}
                              {(event.details.total_cost !== undefined && event.details.total_cost > 0) && (
                                <div className="flex justify-between items-center text-sm pt-2 border-t border-orange-200">
                                  <span className="font-bold text-orange-800">Total Cost</span>
                                  <span className="font-bold text-orange-900">${Number(event.details.total_cost).toFixed(2)}</span>
                                </div>
                              )}
                              
                              {/* SASE Required */}
                              <div className="flex justify-between items-center text-sm">
                                <span className="font-medium text-orange-800">SASE Required</span>
                                <span className={`font-semibold ${event.details.sase_required ? 'text-amber-600' : 'text-green-600'}`}>
                                  {event.details.sase_required ? 'Yes' : 'No'}
                                </span>
                              </div>
                              
                              {/* Parcel Numbers */}
                              {event.details.parcel_numbers && event.details.parcel_numbers.length > 0 && (
                                <div className="flex justify-between items-center text-sm">
                                  <span className="font-medium text-orange-800">Parcels</span>
                                  <span className="font-semibold text-orange-900 text-right max-w-[200px] truncate">
                                    {event.details.parcel_numbers.join(', ')}
                                  </span>
                                </div>
                              )}
                              
                              {/* Request ID for audit */}
                              {event.details.request_id && (
                                <p className="text-xs text-orange-600 pt-2 border-t border-orange-200">
                                  Request ID: {event.details.request_id}
                                </p>
                              )}
                            </div>
                          </div>
                        )}
                        
                        {/* Status Change - Reason Box (when reason exists) */}
                        {event.type === 'status.change' && event.details.reason && (
                          <div className="mt-3 bg-gradient-to-br from-blue-50 to-blue-100/50 rounded-lg p-4 border border-blue-200">
                            <div className="flex items-start gap-2">
                              <svg className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="flex-1">
                                <p className="text-xs font-medium text-blue-700 mb-1">Reason:</p>
                                <p className="text-sm text-blue-900">{event.details.reason}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Clarification Re-Open - Reason / Notes Box */}
                        {event.type === 'status.clarification_reopen' && event.details?.reason && (
                          <div
                            className="mt-3 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 border border-purple-200"
                            data-testid="activity-clarification-reopen-reason"
                          >
                            <div className="flex items-start gap-2">
                              <svg className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                              </svg>
                              <div className="flex-1">
                                <p className="text-xs font-medium text-purple-700 mb-1">Re-Open Reason:</p>
                                <p className="text-sm text-purple-900 whitespace-pre-wrap">{event.details.reason}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Mail-Away Response Received - Details Box */}
                        {event.type === 'status.mail_away_received' && event.details?.response_details && (
                          <div className="mt-3 bg-gradient-to-br from-orange-50 to-orange-100/50 rounded-lg p-4 border border-orange-200">
                            <div className="flex items-start gap-2">
                              <svg className="w-4 h-4 text-orange-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                              </svg>
                              <div className="flex-1">
                                <p className="text-xs font-medium text-orange-700 mb-1">Response Details:</p>
                                <p className="text-sm text-orange-900">{event.details.response_details}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Clarification Response Received - Details Box */}
                        {event.type === 'status.clarification_received' && event.details?.response_details && (
                          <div className="mt-3 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 border border-purple-200">
                            <div className="flex items-start gap-2">
                              <svg className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                              </svg>
                              <div className="flex-1">
                                <p className="text-xs font-medium text-purple-700 mb-1">Response Details:</p>
                                <p className="text-sm text-purple-900">{event.details.response_details}</p>
                              </div>
                            </div>
                          </div>
                        )}
                        
                        {/* Clarification Response Received - Audit Details (Legacy type) */}
                        {event.type === 'clarification.response_received' && event.details && (
                          <div className="mt-3 bg-gradient-to-br from-purple-50 to-purple-100/50 rounded-lg p-4 border border-purple-200">
                            <div className="space-y-3">
                              {/* Response Details / Notes */}
                              {event.details.response_details && (
                                <div className="flex items-start gap-2">
                                  <svg className="w-4 h-4 text-purple-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z" />
                                  </svg>
                                  <div className="flex-1">
                                    <p className="text-xs font-medium text-purple-700 mb-1">Notes:</p>
                                    <p className="text-sm text-purple-900 whitespace-pre-wrap">{event.details.response_details}</p>
                                  </div>
                                </div>
                              )}
                              
                              {/* Attachment Info */}
                              {event.details.document_uploaded && (
                                <div className="flex items-center gap-2 pt-2 border-t border-purple-200">
                                  <svg className="w-4 h-4 text-purple-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.172 7l-6.586 6.586a2 2 0 102.828 2.828l6.414-6.586a4 4 0 00-5.656-5.656l-6.415 6.585a6 6 0 108.486 8.486L20.5 13" />
                                  </svg>
                                  <p className="text-sm font-medium text-purple-800">📎 {event.details.document_uploaded}</p>
                                </div>
                              )}
                              
                              {/* Recorded By (if different from actor) */}
                              {event.details.received_by_role && (
                                <div className="flex items-center gap-2 text-xs text-purple-600 pt-2 border-t border-purple-200">
                                  <span>Recorded by:</span>
                                  <span className="font-semibold capitalize">{event.details.received_by_role.replace(/_/g, ' ')}</span>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {/* Premium Empty State */}
        {!loading && !error && events.length === 0 && (
          <div className="text-center py-10">
            <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl flex items-center justify-center shadow-sm">
              <Clock className="w-8 h-8 text-purple-500" />
            </div>
            <h3 className="text-base font-bold text-slate-900 mb-2">No Activity Yet</h3>
            <p className="text-sm text-slate-600 max-w-md mx-auto">
              Activity will appear here as the order progresses. All edits, status changes, and updates will be tracked in this timeline.
            </p>
          </div>
        )}
        
        {/* Premium Pagination */}
        {!loading && !error && totalPages > 1 && (
          <div className="mt-8 pt-6 border-t border-slate-200">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
              <p className="text-sm font-medium text-slate-600">
                Showing <span className="text-slate-900 font-semibold">{((page - 1) * eventsPerPage) + 1}–{Math.min(page * eventsPerPage, totalEvents)}</span> of <span className="text-slate-900 font-semibold">{totalEvents}</span> events
              </p>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  <svg className="w-4 h-4 inline mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                  </svg>
                  Previous
                </button>
                <div className="px-3 py-1.5 text-sm font-semibold text-purple-700 bg-purple-50 rounded-lg border border-purple-200">
                  {page} of {totalPages}
                </div>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-sm"
                >
                  Next
                  <svg className="w-4 h-4 inline ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      
      {/* Scroll to Top Button for Activity page */}
      <ScrollToTopButton />
    </div>
  );
};


// ============================================
// EXPORTS
// ============================================
export { OverviewTab, SubmissionsTab, FulfillmentTab, ActivityTab };
